import React, { useState, useEffect, useRef } from "react";
import DashNavbar from "./DashNavbar";
import Sidebar from "./Sidebar";
import "../css/dash.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/PDFViewer.css";
import { Pagination, Spinner, Button, Modal, Card, Table, Form, Alert, InputGroup, Row, Col, Badge } from "react-bootstrap";
import axios from 'axios';
import Chart from "chart.js/auto";

// --- GLOBAL OPTIONS FOR MODAL ---
const jobStatusOptions = [
    { value: "applied", label: "Applied", bgColor: "bg-info" },
    { value: "under_review", label: "Under Review", bgColor: "bg-secondary" },
    { value: "interview_scheduled", label: "Interview Scheduled", bgColor: "bg-warning text-dark" },
    { value: "selected", label: "Selected", bgColor: "bg-success" },
    { value: "rejected", label: "Rejected", bgColor: "bg-danger" },
];

// --- HELPER FUNCTION TO FORMAT OPPORTUNITY TYPE ---
const formatOpportunityType = (type) => {
    if (!type) return 'N/A';
    switch (type) {
        case 'Business_Opportunity':
            return 'Business';
        case 'Technical_Consultant':
            return 'Consultant';
        case 'Job_Opening':
            return 'Job';
        default:
            return type;
    }
};

// Helper function for ApplicantDetailsModal
const renderField = (label, value) => {
    if (!value && value !== 0) return null;
    return (
        <Col md={6} className="mb-2">
            <div className="p-2 bg-light rounded">
                <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
                    {label}
                </small>
                <span className="text-dark">{value}</span>
            </div>
        </Col>
    );
};

// --- APPLICANT DETAILS MODAL (Profile Data) ---
const ApplicantDetailsModal = ({ show, onHide, applicant }) => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (show && applicant) {
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 500);
        }
    }, [show, applicant]);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="gradient-text">
                    <i className="fas fa-user-circle me-2"></i>
                    {applicant ? `${applicant.first_name || ''} ${applicant.last_name || ''}` : "Applicant Profile"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Fetching profile details...</p>
                    </div>
                ) : applicant ? (
                    <div className="px-2">
                        {/* Personal Information */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Personal Details</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Full Name", `${applicant.first_name || ''} ${applicant.middle_name || ''} ${applicant.last_name || ''}`)}
                                    {renderField("Email Address", applicant.email)}
                                    {renderField("Phone Number", applicant.phone)}
                                    {renderField("Gender", applicant.gender)}
                                    {renderField("Date of Birth", applicant.date_of_birth)}
                                    {renderField("Age", applicant.age)}
                                    {renderField("Marital Status", applicant.marital_status)}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Address Information */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Address & Location</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Current Location", applicant.address_type)}
                                    {renderField("Village/City", applicant.village)}
                                    {renderField("Tehsil", applicant.tehsil)}
                                    {renderField("District", applicant.district)}
                                    {renderField("State", applicant.state)}
                                    {renderField("Country", applicant.country)}
                                    {renderField("Zip Code", applicant.zip_code)}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Professional & Education */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Education & Experience</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Education Level", applicant.education)}
                                    {renderField("Degree", applicant.degree)}
                                    {renderField("Experience Type", <Badge bg={applicant.experience === 'Experienced' ? 'info' : 'secondary'}>{applicant.experience}</Badge>)}
                                    {applicant.experience === 'Experienced' && (
                                        <>
                                            {renderField("Total Experience", `${applicant.Work_total_experience_year || 0} Years ${applicant.Work_total_experience_months || 0} Months`)}
                                            {renderField("Current Company", applicant.current_comapny_name)}
                                            {renderField("Current Designation", applicant.job_title)}
                                            {renderField("Notice Period", `${applicant.current_notice_period_days} Days`)}
                                        </>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Financials (Only for Experienced) */}
                        {applicant.experience === 'Experienced' && (
                            <Card className="shadow-sm mb-3 border-0">
                                <Card.Header className="bg-white fw-bold text-primary">Financial Details & Reason for Leaving</Card.Header>
                                <Card.Body>
                                    <Row>
                                        {renderField("Current CTC", applicant.current_ctc)}
                                        {renderField("Gross Salary", applicant.gross)}
                                        {renderField("Net Salary", applicant.net)}
                                        {renderField("Variables", applicant.variables)}
                                        <Col md={12} className="mt-2">
                                            <div className="p-2 bg-light rounded">
                                                <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>Reason for Leaving</small>
                                                <span className="text-dark">{applicant.reason_for_leaving || "No reason provided"}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Referral */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Referral & Other</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Referred By", applicant.referred_by)}
                                    {renderField("Referrer Contact", applicant.referred_contact)}
                                    {renderField("Acknowledged", applicant.acknowledged ? "Yes" : "No")}
                                    {renderField("Job Offered", applicant.job_offered ? <Badge bg="success">Yes</Badge> : <Badge bg="danger">No</Badge>)}
                                </Row>
                            </Card.Body>
                        </Card>
                    </div>
                ) : (
                    <div className="text-center p-4">No data found for this applicant.</div>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onHide}>Close Profile</Button>
            </Modal.Footer>
        </Modal>
    );
};

// --- APPLICANT RESULTS MODAL (Personality Assessments) ---
const ApplicantModal = ({ show, onHide, applicant, jobapplications, setSuccessMessage, updateJobApplication }) => {
    const chartRefs = useRef({});
    const [personalityData, setPersonalityData] = useState([]);
    const [personalityJobTitles, setPersonalityJobTitles] = useState([]);
    const [allSubmittedAnswers, setAllSubmittedAnswers] = useState([]);
    const [filteredSubmittedAnswers, setFilteredSubmittedAnswers] = useState(null);
    const [currentJobId, setCurrentJobId] = useState(null);
    const [jobQuestions, setJobQuestions] = useState([]);
    const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);
    const [isLoadingPersonality, setIsLoadingPersonality] = useState(false);

    useEffect(() => {
        return () => {
            Object.values(chartRefs.current).forEach(chart => chart?.destroy());
            chartRefs.current = {};
        };
    }, []);

    useEffect(() => {
        if (show && applicant?.id) {
            fetchPersonalityData(applicant.id);
        }
    }, [show, applicant]);
    
    useEffect(() => {
        Object.values(chartRefs.current).forEach(chart => chart?.destroy());
        chartRefs.current = {};

        if (Array.isArray(personalityData) && personalityData.length > 0) {
            personalityData.forEach((data, index) => {
                const chartId = `personalityChart-${applicant?.id}-${index}`;
                const chartElement = document.getElementById(chartId);
                if (chartElement && data?.job_id) {
                    const newChart = createDonutChart(data, chartId);
                    if (newChart) chartRefs.current[index] = newChart;
                }
            });
        }
    }, [personalityData, applicant?.id]);

    useEffect(() => {
        if (allSubmittedAnswers.length > 0 && currentJobId !== null) {
            const filtered = allSubmittedAnswers.find(item => item.job_id === currentJobId || item.posting_id === currentJobId);
            setFilteredSubmittedAnswers(filtered || null);
        } else {
            setFilteredSubmittedAnswers(null);
        }
    }, [allSubmittedAnswers, currentJobId]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            await axios.put(`https://raasbackend.vetrinahealthcare.com/applicants/update_status/${id}/`, { 
                job_status: newStatus 
            });
            
            setSuccessMessage("Job status updated successfully");
            updateJobApplication(id, newStatus);
            
            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (error) {
            console.error("Error updating job status:", error);
            alert("Failed to update job status. Please try again.");
        }
    };

    const fetchPersonalityData = async (applicantId) => {
        if (!applicantId) return;
        
        setIsLoadingPersonality(true);
        try {
            const personalityResponse = await axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`);
            
            const responseData = personalityResponse.data;
            let fetchedPersonalityData = [];
            
            if (responseData && responseData.results) {
                fetchedPersonalityData = responseData.results || [];
            } else if (Array.isArray(responseData)) {
                fetchedPersonalityData = responseData;
            }
            
            setPersonalityData(fetchedPersonalityData);

            if (fetchedPersonalityData.length > 0) {
                const jobTitles = fetchedPersonalityData.map(data => {
                    if (data.job_title) return data.job_title;
                    else if (data.opening_details?.jobtitle) return data.opening_details.jobtitle;
                    else return `Job ID: ${data.job_id || data.posting_id}`;
                });
                setPersonalityJobTitles(jobTitles);
            }
        } catch (error) {
            console.error("Error fetching personality data:", error);
        } finally {
            setIsLoadingPersonality(false);
        }
    };
    
    const handleShowSubmittedAnswers = async (applicantId, jobId) => {
        setCurrentJobId(jobId);
        setIsLoadingAnswers(true);
        try {
            const answersResponse = await axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`);
            
            let answersData = [];
            const answersResponseData = answersResponse.data;
            
            if (answersResponseData && answersResponseData.results) {
                answersData = answersResponseData.results || [];
            } else if (Array.isArray(answersResponseData)) {
                answersData = answersResponseData;
            }
            
            setAllSubmittedAnswers(answersData);
            
            const currentAssessment = answersData.find(item => 
                item.job_id === jobId || item.posting_id === jobId
            );
            
            if (!currentAssessment) {
                setJobQuestions([]);
                setIsLoadingAnswers(false);
                return;
            }
            
            const openingPost = currentAssessment.opening_post;
            
            if (!openingPost) {
                setJobQuestions([]);
                setIsLoadingAnswers(false);
                return;
            }
            
            const questionResponse = await axios.get(
                `https://raasbackend.vetrinahealthcare.com/questions/${openingPost}/${jobId}/?applicant_id=${applicantId}`
            );
            
            const questionsData = questionResponse.data;
            if (questionsData && questionsData.questions) {
                setJobQuestions(questionsData.questions || []);
            } else if (Array.isArray(questionsData)) {
                setJobQuestions(questionsData);
            } else {
                setJobQuestions([]);
            }
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setJobQuestions([]);
        } finally {
            setIsLoadingAnswers(false);
        }
    };
    
    const createDonutChart = (data, chartId) => {
        const ctx = document.getElementById(chartId)?.getContext("2d");
        if (!ctx || !data) return null;
        
        const correctCount = data.correct_count || 0;
        const wrongCount = data.wrong_count || 0;
        const total = correctCount + wrongCount;
        
        if (total === 0) return null;

        return new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: [`Correct (${correctCount})`, `Wrong (${wrongCount})`],
                datasets: [{
                    data: [correctCount, wrongCount],
                    backgroundColor: ['rgba(40, 167, 69, 0.8)', 'rgba(220, 53, 69, 0.8)'],
                    borderColor: ['rgba(40, 167, 69, 1)', 'rgba(220, 53, 69, 1)'],
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '60%',
                plugins: { legend: { display: false } }
            }
        });
    };

    const renderAnswerSummary = (question, index) => {
        const userAnswer = question.user_selected_answer;
        
        if (!userAnswer) {
            return (
                <div className="text-center p-3">
                    <Badge bg="secondary">No answer data available</Badge>
                </div>
            );
        }

        const selectedIndex = userAnswer.selected_index;
        const isCorrect = userAnswer.is_correct === "correct";
        const correctIndex = question.correct_answer_index;
        const options = question.answers;

        return (
            <div className="p-2">
                <div className="mb-1">
                    <small className="text-muted fw-bold">Options:</small>
                </div>
                <div className="border rounded p-2">
                    {options.map((option, idx) => {
                        const isSelected = idx === selectedIndex;
                        const isCorrectOpt = idx === correctIndex;
                        
                        return (
                            <div key={idx} className={`d-flex align-items-center p-2 mb-1 rounded ${isSelected && isCorrectOpt ? 'bg-success bg-opacity-25 border border-success' : isSelected ? 'bg-danger bg-opacity-25 border border-danger' : isCorrectOpt ? 'bg-success bg-opacity-10 border border-success' : ''}`}>
                                <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${isSelected ? (isCorrectOpt ? 'bg-success text-white' : 'bg-danger text-white') : isCorrectOpt ? 'bg-success text-white' : 'bg-light text-dark border'}`}
                                     style={{ width: '24px', height: '24px', fontSize: '12px', flexShrink: 0 }}>
                                    {String.fromCharCode(65 + idx)}
                                </div>
                                <div className="flex-grow-1">
                                    <span>{option}</span>
                                </div>
                                <div className="ms-2">
                                    {isSelected && isCorrectOpt && <Badge bg="success">✓ Your Answer (Correct)</Badge>}
                                    {isSelected && !isCorrectOpt && <Badge bg="danger">✗ Your Answer (Wrong)</Badge>}
                                    {!isSelected && isCorrectOpt && <Badge bg="success">✓ Correct Answer</Badge>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                <div className="mt-3 pt-2 border-top">
                    <div className="row small">
                        <div className="col-6 text-start">
                            <span className="text-muted">Selected:</span>
                            <div className={`d-inline-block ms-2 fw-bold ${isCorrect ? 'text-success' : 'text-danger'}`}>
                                Option {String.fromCharCode(65 + selectedIndex)}
                            </div>
                        </div>
                        <div className="col-6 text-end">
                            <span className="text-muted">Correct:</span>
                            <div className="d-inline-block ms-2 fw-bold text-success">
                                Option {String.fromCharCode(65 + correctIndex)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onHide} dialogClassName="modal-xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="gradient-text">
                    {applicant?.first_name || 'Applicant'} Assessment Results
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">Job Applications</h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <Table striped bordered hover responsive className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Job Title</th>
                                    <th>Status</th>
                                    <th>Applied On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobapplications.length > 0 ? (
                                    jobapplications.map(jobApp => {
                                        const statusOption = jobStatusOptions.find(opt => opt.value === jobApp.job_status);
                                        
                                        return (
                                            <tr key={jobApp.id}>
                                                <td>{jobApp.job_title || 'N/A'}</td>
                                                <td>
                                                    <Form.Select 
                                                        size="sm" 
                                                        className={statusOption?.bgColor || ''}
                                                        value={jobApp.job_status} 
                                                        onChange={(e) => handleStatusChange(jobApp.id, e.target.value)}
                                                        style={{ 
                                                            color: statusOption?.bgColor.includes('text-dark') ? '#000' : '#fff',
                                                            minWidth: '150px'
                                                        }}
                                                    >
                                                        {jobStatusOptions.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    {jobApp.applied_date ? 
                                                        new Date(jobApp.applied_date).toLocaleDateString('en-GB') : 
                                                        'N/A'
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center p-3 text-muted">
                                            No job applications found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                <h5 className="gradient-text mt-4 mb-3">Personality Assessments</h5>
                
                {isLoadingPersonality ? (
                    <div className="text-center p-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Loading assessment data...</p>
                    </div>
                ) : personalityData.length > 0 ? (
                    <div className="d-flex flex-wrap justify-content-center gap-3">
                        {personalityData.map((data, index) => {
                            const jobId = data.job_id || data.posting_id;
                            const jobTitle = personalityJobTitles[index] || `Assessment ${index + 1}`;
                            
                            return data && jobId ? (
                                <Card key={`${jobId}-${index}`} className="text-center shadow-sm border-0" style={{ minWidth: '240px', flex: '1 1 240px' }}>
                                    <Card.Header className="bg-primary text-white">
                                        <Card.Title as="h6" className="mb-0 text-truncate" title={jobTitle}>
                                            {jobTitle}
                                        </Card.Title>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        <div style={{ height: '120px', width: '100%' }}>
                                            <canvas id={`personalityChart-${applicant?.id}-${index}`}></canvas>
                                        </div>
                                        <div className="mt-3">
                                            <h4 className="gradient-text fw-bold mb-2">
                                                {data.status ? `${data.status.toFixed(1)}%` : 'N/A'}
                                            </h4>
                                            <div className="d-flex justify-content-center gap-3 small text-muted">
                                                <span className="text-success">
                                                    <i className="fas fa-check-circle"></i> {data.correct_count || 0} Correct
                                                </span>
                                                <span className="text-danger">
                                                    <i className="fas fa-times-circle"></i> {data.wrong_count || 0} Wrong
                                                </span>
                                            </div>
                                        </div>
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            className="mt-3"
                                            onClick={() => handleShowSubmittedAnswers(applicant.id, jobId)}
                                            disabled={isLoadingAnswers && currentJobId === jobId}
                                        >
                                            {isLoadingAnswers && currentJobId === jobId ? (
                                                <>
                                                    <Spinner size="sm" animation="border" className="me-2" />
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-eye me-2"></i>
                                                    View Answers
                                                </>
                                            )}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            ) : null;
                        })}
                    </div>
                ) : (
                    <div className="text-center p-4 border rounded bg-light">
                        <i className="fas fa-chart-pie fa-2x text-muted mb-3"></i>
                        <p className="text-muted mb-0">No assessment data available.</p>
                    </div>
                )}

                {currentJobId && (
                    <div className="mt-4">
                        <Card className="shadow-sm border-0">
                            <Card.Header className="bg-info text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="fas fa-list-check me-2"></i>
                                        Submitted Answers
                                    </h5>
                                    <Badge bg="light" text="dark">
                                        Score: {filteredSubmittedAnswers?.status || 0}%
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {isLoadingAnswers ? (
                                    <div className="text-center p-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2">Loading answers...</p>
                                    </div>
                                ) : jobQuestions.length > 0 ? (
                                    <div className="row g-3">
                                        {jobQuestions.map((question, index) => {
                                            const userAnswer = question.user_selected_answer;
                                            const isCorrect = userAnswer?.is_correct === "correct";
                                            
                                            return (
                                                <div key={question.id || index} className="col-12">
                                                    <Card className="border">
                                                        <Card.Body>
                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                <div>
                                                                    <Badge bg="secondary" className="me-2">Q{index + 1}</Badge>
                                                                    <span className="fw-bold">{question.question_text}</span>
                                                                </div>
                                                                <Badge bg={isCorrect ? "success" : "danger"} className="fs-6 px-3 py-2">
                                                                    {isCorrect ? "✓ Correct" : "✗ Wrong"}
                                                                </Badge>
                                                            </div>
                                                            
                                                            {renderAnswerSummary(question, index)}
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : filteredSubmittedAnswers ? (
                                    <Card className="border-0 bg-light">
                                        <Card.Body className="text-center">
                                            <div className="d-flex justify-content-center gap-4 mb-3">
                                                <div className="text-center">
                                                    <div className="display-6 text-success">{filteredSubmittedAnswers.correct_count || 0}</div>
                                                    <small className="text-muted">Correct</small>
                                                </div>
                                                <div className="text-center">
                                                    <div className="display-6 text-danger">{filteredSubmittedAnswers.wrong_count || 0}</div>
                                                    <small className="text-muted">Wrong</small>
                                                </div>
                                                <div className="text-center">
                                                    <div className="display-6 text-primary">{filteredSubmittedAnswers.all_count || 0}</div>
                                                    <small className="text-muted">Total</small>
                                                </div>
                                            </div>
                                            <p className="text-muted mb-0">
                                                Detailed question data not available
                                            </p>
                                        </Card.Body>
                                    </Card>
                                ) : (
                                    <div className="text-center p-4">
                                        <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                                        <p className="text-muted mb-0">No answers found for this assessment.</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onHide}>
                    <i className="fas fa-times me-2"></i>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};



// --- MAIN INTERVIEWER DASHBOARD COMPONENT ---
function InterviewerDashboard() {
    const [interviewSchedule, setInterviewSchedule] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5); // Now as state to allow dynamic change
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingTable, setLoadingTable] = useState(true);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [loadingView, setLoadingView] = useState({});
    const [loadingResume, setLoadingResume] = useState({});
    const [selectedApplicantForResults, setSelectedApplicantForResults] = useState(null);
    const [jobApplicationsForModal, setJobApplicationsForModal] = useState([]);
    const [loadingResults, setLoadingResults] = useState({});
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => { 
        fetchInterviewSchedule(); 
    }, []);

    const fetchInterviewSchedule = async () => {
        const storedEmail = localStorage.getItem("email");
        if (!storedEmail) {
            setLoadingTable(false);
            return;
        }
        setLoadingTable(true);
        try {
            // Step 1: Fetch email from UserProfile API
            const profileResponse = await axios.post("https://raasbackend.vetrinahealthcare.com/UserProfile/", { email: storedEmail });
            const profileEmail = profileResponse.data?.user_data?.email || profileResponse.data?.email;
            
            if (!profileEmail) {
                console.error("Email not found in UserProfile response");
                setLoadingTable(false);
                return;
            }

            // Step 2: Fetch interviews using the email from UserProfile
            const interviewResponse = await axios.get(`https://raasbackend.vetrinahealthcare.com/interviews/${profileEmail}/`);
            const interviews = Array.isArray(interviewResponse.data) ? interviewResponse.data : [interviewResponse.data];
            
            const formattedData = interviews.map(interview => ({
                ...interview,
                date: formatDate(interview.date),
                time: formatTime(interview.time)
            }));
            
            setInterviewSchedule(formattedData);
        } catch (error) {
            console.error("Error fetching schedule:", error);
        } finally {
            setLoadingTable(false);
        }
    };

    const handleViewResultsClick = async (interview) => {
        const email = interview.candidate_email;
        if (!email) return;
        
        setLoadingResults(prev => ({ ...prev, [email]: true }));
        try {
            const applicantRes = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
            
            if (!applicantRes.data || !applicantRes.data.id) {
                alert("Applicant data not found");
                return;
            }
            
            setSelectedApplicantForResults(applicantRes.data);
            
            const jobsRes = await axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantRes.data.id}/`);
            setJobApplicationsForModal(jobsRes.data || []);
            
            setShowResultsModal(true);
        } catch (error) {
            console.error("Error fetching data for results modal:", error);
            alert("Failed to load applicant results. Please try again.");
        } finally {
            setLoadingResults(prev => ({ ...prev, [email]: false }));
        }
    };
    
    const updateJobApplicationStatusInList = (jobAppId, newStatus) => {
        setJobApplicationsForModal(prev => 
            prev.map(app => app.id === jobAppId ? { ...app, job_status: newStatus } : app)
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-GB');
        } catch (e) {
            return dateString;
        }
    };
    
    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours, 10);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const hour12 = hour % 12 || 12;
            return `${hour12}:${minutes} ${ampm}`;
        } catch (e) {
            return timeString;
        }
    };

    const handleViewClick = async (email) => {
        setLoadingView(prev => ({ ...prev, [email]: true }));
        try {
            const response = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
            
            if (response.data) {
                setSelectedApplicant(response.data);
                setShowProfileModal(true);
            } else {
                alert("No applicant data found");
            }
        } catch (error) {
            console.error("Error fetching applicant data:", error);
            alert("Failed to load applicant details. Please try again.");
        } finally {
            setLoadingView(prev => ({ ...prev, [email]: false }));
        }
    };

    const handleViewResume = async (email) => {
        setLoadingResume(prev => ({ ...prev, [email]: true }));
        try {
            const response = await axios.post(
                "https://raasbackend.vetrinahealthcare.com/view_resume/", 
                { email },
                { responseType: 'blob' }
            );
            
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error fetching resume:", error);
            alert("Failed to load resume. Please try again.");
        } finally {
            setLoadingResume(prev => ({ ...prev, [email]: false }));
        }
    };

    // Pagination and Search logic
    const filteredSchedule = interviewSchedule.filter(i => 
        Object.values(i).some(val => 
            String(val).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalItems = filteredSchedule.length;
    const firstIndex = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const lastIndex = Math.min(currentPage * itemsPerPage, totalItems);
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    const currentItems = filteredSchedule.slice(
        (currentPage - 1) * itemsPerPage, 
        currentPage * itemsPerPage
    );

    // Generating dynamic page size options based on totalItems
    const getPageOptions = () => {
        const baseOptions = [5, 10, 20, 30, 40, 50];
        if (totalItems > 50) baseOptions.push(100);
        if (totalItems > 100) baseOptions.push(200);
        // Include current itemsPerPage if it's not in the list
        if (!baseOptions.includes(itemsPerPage)) baseOptions.push(itemsPerPage);
        return [...new Set(baseOptions)].sort((a, b) => a - b);
    };

    return (
        <>
            <DashNavbar />
            <div className="wrapper bg-light">
                <Sidebar />
                <div className="col-lg-10 col-md-8 col-sm-12 main dash4 px-4 py-3">
                    <div className="container-fluid">
                        {successMessage && (
                            <Alert variant="success" onClose={() => setSuccessMessage("")} dismissible>
                                {successMessage}
                            </Alert>
                        )}
                        
                        {/* --- HERO SECTION --- */}
                        <div className="hero-section bg-white p-4 rounded-4 shadow-sm mb-4" style={{ borderLeft: '5px solid #f58220' }}>
                            <h2 className="fw-bold text-dark mb-1" style={{color: "#fab62dff"}}>Welcome to your Dashboard</h2>
                            <p className="text-muted mb-0">
                                Below is your interview schedule. You currently have <strong>{totalItems}</strong> candidate(s) to review.
                            </p>
                        </div>
                        
                        <div className="bg-white p-4 rounded-4 shadow-sm w-100">
                            {/* --- HEADER & SEARCH --- */}
                            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                                <h3 className="fw-bold text-primary mb-0">
                                    Interview Schedule
                                </h3>   
                                <div style={{ minWidth: '350px' }}>
                                    <InputGroup className="shadow-sm border rounded-pill overflow-hidden">
                                        <InputGroup.Text className="bg-white border-0 ps-3">
                                            <i className="fas fa-search text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control 
                                            className="border-0 shadow-none py-2"
                                            type="search" 
                                            placeholder="Search by name, email, job title..." 
                                            value={searchQuery} 
                                            onChange={e => {setSearchQuery(e.target.value); setCurrentPage(1);}} 
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                            
                            {/* --- TABLE --- */}
                            <div className="table-responsive">
                                <Table striped bordered hover className="align-middle border rounded overflow-hidden mb-0" style={{ width: '100%', tableLayout: 'fixed' }}>
                                    <thead className="table-light">
                                        <tr className="text-secondary small fw-bold text-uppercase">
                                            <th className="py-3 text-nowrap" style={{ width: '5%' }}>Sr.No</th>
                                            <th className="py-3 text-nowrap" style={{ width: '15%' }}>Candidate</th>
                                            <th className="py-3 text-nowrap" style={{ width: '10%' }}>Date</th>
                                            <th className="py-3 text-nowrap" style={{ width: '8%' }}>Time</th>
                                            <th className="py-3 text-nowrap" style={{ width: '12%' }}>Applied Job</th>
                                            <th className="py-3 text-nowrap" style={{ width: '10%' }}>Opportunity Type</th>
                                            <th className="py-3 text-nowrap" style={{ width: '13%' }}>Meeting</th>
                                            <th className="py-3 text-center text-nowrap" style={{ width: '27%' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingTable ? (
                                            <tr>
                                                <td colSpan="8" className="text-center p-5">
                                                    <Spinner animation="border" variant="primary" />
                                                    <p className="mt-2">Loading schedule...</p>
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((interview, index) => (
                                                <tr key={interview.id || index}>
                                                    <td className="text-nowrap text-truncate">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                    <td className="fw-bold text-nowrap text-truncate" title={interview.candidate}>{interview.candidate}</td>
                                                    <td className="text-nowrap text-truncate">{interview.date}</td>
                                                    <td className="text-nowrap text-truncate">{interview.time}</td>
                                                    <td className="text-nowrap text-truncate" title={interview.job_title}>{interview.job_title || 'N/A'}</td>
                                                    <td className="text-nowrap text-truncate">{formatOpportunityType(interview.opening_post)}</td>
                                                    <td className="text-nowrap">
                                                        {interview.meetLink ? (
                                                            <a 
                                                                href={interview.meetLink} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer" 
                                                                className="btn btn-primary btn-sm rounded-pill px-3"
                                                            >
                                                                Join Meeting
                                                            </a>
                                                        ) : (
                                                            <Badge bg="secondary" className="px-3 rounded-pill">{interview.mode || 'Offline'}</Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-nowrap">
                                                        <div className="d-flex justify-content-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm" 
                                                                onClick={() => handleViewClick(interview.candidate_email)} 
                                                                disabled={loadingView[interview.candidate_email]}
                                                            >
                                                                {loadingView[interview.candidate_email] ? (
                                                                    <Spinner size="sm" animation="border" />
                                                                ) : "View Profile"}
                                                            </Button>
                                                            
                                                            <Button 
                                                                variant="info" 
                                                                size="sm" 
                                                                className="text-white"
                                                                onClick={() => handleViewResultsClick(interview)} 
                                                                disabled={loadingResults[interview.candidate_email]}
                                                            >
                                                                {loadingResults[interview.candidate_email] ? (
                                                                    <Spinner size="sm" animation="border" />
                                                                ) : "Results"}
                                                            </Button>
                                                            
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm" 
                                                                onClick={() => handleViewResume(interview.candidate_email)} 
                                                                disabled={loadingResume[interview.candidate_email]}
                                                            >
                                                                {loadingResume[interview.candidate_email] ? (
                                                                    <Spinner size="sm" animation="border" />
                                                                ) : "Resume"}
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center p-5 text-muted">
                                                    No interviews scheduled.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            
                            {/* --- PAGINATION SECTION --- */}
                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top flex-wrap gap-2" style={{ fontSize: '13px' }}>
                                <div className="text-muted">
                                    Showing <strong>{firstIndex}</strong> to <strong>{lastIndex}</strong> of <strong>{totalItems}</strong> results
                                </div>
                                
                                <div className="d-flex align-items-center gap-3">
                                    {/* Rows per page */}
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>Rows per page:</span>
                                        <Form.Select 
                                            size="sm" 
                                            className="shadow-none" 
                                            style={{ 
                                                width: '68px', 
                                                cursor: 'pointer', 
                                                fontSize: '13px',
                                                borderRadius: '6px',
                                                border: '1px solid #dee2e6',
                                                padding: '4px 8px'
                                            }}
                                            value={itemsPerPage}
                                            onChange={(e) => {setItemsPerPage(Number(e.target.value)); setCurrentPage(1);}}
                                        >
                                            {getPageOptions().map(val => (
                                                <option key={val} value={val}>{val}</option>
                                            ))}
                                        </Form.Select>
                                    </div>

                                    {/* Divider */}
                                    <div style={{ width: '1px', height: '24px', background: '#dee2e6' }}></div>

                                    {/* Page info */}
                                    <span className="text-muted fw-medium" style={{ whiteSpace: 'nowrap' }}>
                                        Page {currentPage} of {totalPages || 1}
                                    </span>

                                    {/* Divider */}
                                    <div style={{ width: '1px', height: '24px', background: '#dee2e6' }}></div>

                                    {/* Navigation buttons */}
                                    <div className="d-flex align-items-center gap-1">
                                        <button 
                                            className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                            style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(1)}
                                            title="First page"
                                        >
                                            <span style={{ fontSize: '14px', lineHeight: 1 }}>«</span>
                                        </button>
                                        <button 
                                            className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                            style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            title="Previous page"
                                        >
                                            <span style={{ fontSize: '14px', lineHeight: 1 }}>‹</span>
                                        </button>
                                        <button 
                                            className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                            style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                                            disabled={currentPage === totalPages || totalPages === 0}
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            title="Next page"
                                        >
                                            <span style={{ fontSize: '14px', lineHeight: 1 }}>›</span>
                                        </button>
                                        <button 
                                            className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                            style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                                            disabled={currentPage === totalPages || totalPages === 0}
                                            onClick={() => setCurrentPage(totalPages)}
                                            title="Last page"
                                        >
                                            <span style={{ fontSize: '14px', lineHeight: 1 }}>»</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <ApplicantDetailsModal 
                applicant={selectedApplicant || {}} 
                show={showProfileModal} 
                onHide={() => setShowProfileModal(false)} 
            />
            
            {showResultsModal && (
                <ApplicantModal 
                    show={showResultsModal} 
                    onHide={() => setShowResultsModal(false)} 
                    applicant={selectedApplicantForResults} 
                    jobapplications={jobApplicationsForModal} 
                    setSuccessMessage={setSuccessMessage} 
                    updateJobApplication={updateJobApplicationStatusInList} 
                />
            )}
        </>
    );
}

export default InterviewerDashboard;


// import React, { useState, useEffect, useRef } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Pagination, Spinner, Button, Modal, Card, Table, Form, Alert, InputGroup, Row, Col, Badge } from "react-bootstrap";
// import axios from 'axios';
// import Chart from "chart.js/auto";

// // --- GLOBAL OPTIONS FOR MODAL ---
// const jobStatusOptions = [
//     { value: "applied", label: "Applied", bgColor: "bg-info" },
//     { value: "under_review", label: "Under Review", bgColor: "bg-secondary" },
//     { value: "interview_scheduled", label: "Interview Scheduled", bgColor: "bg-warning text-dark" },
//     { value: "selected", label: "Selected", bgColor: "bg-success" },
//     { value: "rejected", label: "Rejected", bgColor: "bg-danger" },
// ];

// // --- HELPER FUNCTION TO FORMAT OPPORTUNITY TYPE ---
// const formatOpportunityType = (type) => {
//     if (!type) return 'N/A';
//     switch (type) {
//         case 'Business_Opportunity':
//             return 'Business';
//         case 'Technical_Consultant':
//             return 'Consultant';
//         case 'Job_Opening':
//             return 'Job';
//         default:
//             return type;
//     }
// };

// // Helper function for ApplicantDetailsModal
// const renderField = (label, value) => {
//     if (!value && value !== 0) return null;
//     return (
//         <Col md={6} className="mb-2">
//             <div className="p-2 bg-light rounded">
//                 <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
//                     {label}
//                 </small>
//                 <span className="text-dark">{value}</span>
//             </div>
//         </Col>
//     );
// };

// // --- APPLICANT DETAILS MODAL (Profile Data) ---
// const ApplicantDetailsModal = ({ show, onHide, applicant }) => {
//     const [isLoading, setIsLoading] = useState(false);

//     useEffect(() => {
//         if (show && applicant) {
//             setIsLoading(true);
//             setTimeout(() => setIsLoading(false), 500);
//         }
//     }, [show, applicant]);

//     return (
//         <Modal show={show} onHide={onHide} size="lg" centered scrollable>
//             <Modal.Header closeButton className="bg-light">
//                 <Modal.Title className="gradient-text">
//                     <i className="fas fa-user-circle me-2"></i>
//                     {applicant ? `${applicant.first_name || ''} ${applicant.last_name || ''}` : "Applicant Profile"}
//                 </Modal.Title>
//             </Modal.Header>
//             <Modal.Body className="bg-light">
//                 {isLoading ? (
//                     <div className="text-center p-5">
//                         <Spinner animation="border" variant="primary" />
//                         <p className="mt-2">Fetching profile details...</p>
//                     </div>
//                 ) : applicant ? (
//                     <div className="px-2">
//                         {/* Personal Information */}
//                         <Card className="shadow-sm mb-3 border-0">
//                             <Card.Header className="bg-white fw-bold text-primary">Personal Details</Card.Header>
//                             <Card.Body>
//                                 <Row>
//                                     {renderField("Full Name", `${applicant.first_name || ''} ${applicant.middle_name || ''} ${applicant.last_name || ''}`)}
//                                     {renderField("Email Address", applicant.email)}
//                                     {renderField("Phone Number", applicant.phone)}
//                                     {renderField("Gender", applicant.gender)}
//                                     {renderField("Date of Birth", applicant.date_of_birth)}
//                                     {renderField("Age", applicant.age)}
//                                     {renderField("Marital Status", applicant.marital_status)}
//                                 </Row>
//                             </Card.Body>
//                         </Card>

//                         {/* Address Information */}
//                         <Card className="shadow-sm mb-3 border-0">
//                             <Card.Header className="bg-white fw-bold text-primary">Address & Location</Card.Header>
//                             <Card.Body>
//                                 <Row>
//                                     {renderField("Current Location", applicant.address_type)}
//                                     {renderField("Village/City", applicant.village)}
//                                     {renderField("Tehsil", applicant.tehsil)}
//                                     {renderField("District", applicant.district)}
//                                     {renderField("State", applicant.state)}
//                                     {renderField("Country", applicant.country)}
//                                     {renderField("Zip Code", applicant.zip_code)}
//                                 </Row>
//                             </Card.Body>
//                         </Card>

//                         {/* Professional & Education */}
//                         <Card className="shadow-sm mb-3 border-0">
//                             <Card.Header className="bg-white fw-bold text-primary">Education & Experience</Card.Header>
//                             <Card.Body>
//                                 <Row>
//                                     {renderField("Education Level", applicant.education)}
//                                     {renderField("Degree", applicant.degree)}
//                                     {renderField("Experience Type", <Badge bg={applicant.experience === 'Experienced' ? 'info' : 'secondary'}>{applicant.experience}</Badge>)}
//                                     {applicant.experience === 'Experienced' && (
//                                         <>
//                                             {renderField("Total Experience", `${applicant.Work_total_experience_year || 0} Years ${applicant.Work_total_experience_months || 0} Months`)}
//                                             {renderField("Current Company", applicant.current_comapny_name)}
//                                             {renderField("Current Designation", applicant.job_title)}
//                                             {renderField("Notice Period", `${applicant.current_notice_period_days} Days`)}
//                                         </>
//                                     )}
//                                 </Row>
//                             </Card.Body>
//                         </Card>

//                         {/* Financials (Only for Experienced) */}
//                         {applicant.experience === 'Experienced' && (
//                             <Card className="shadow-sm mb-3 border-0">
//                                 <Card.Header className="bg-white fw-bold text-primary">Financial Details & Reason for Leaving</Card.Header>
//                                 <Card.Body>
//                                     <Row>
//                                         {renderField("Current CTC", applicant.current_ctc)}
//                                         {renderField("Gross Salary", applicant.gross)}
//                                         {renderField("Net Salary", applicant.net)}
//                                         {renderField("Variables", applicant.variables)}
//                                         <Col md={12} className="mt-2">
//                                             <div className="p-2 bg-light rounded">
//                                                 <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>Reason for Leaving</small>
//                                                 <span className="text-dark">{applicant.reason_for_leaving || "No reason provided"}</span>
//                                             </div>
//                                         </Col>
//                                     </Row>
//                                 </Card.Body>
//                             </Card>
//                         )}

//                         {/* Referral */}
//                         <Card className="shadow-sm mb-3 border-0">
//                             <Card.Header className="bg-white fw-bold text-primary">Referral & Other</Card.Header>
//                             <Card.Body>
//                                 <Row>
//                                     {renderField("Referred By", applicant.referred_by)}
//                                     {renderField("Referrer Contact", applicant.referred_contact)}
//                                     {renderField("Acknowledged", applicant.acknowledged ? "Yes" : "No")}
//                                     {renderField("Job Offered", applicant.job_offered ? <Badge bg="success">Yes</Badge> : <Badge bg="danger">No</Badge>)}
//                                 </Row>
//                             </Card.Body>
//                         </Card>
//                     </div>
//                 ) : (
//                     <div className="text-center p-4">No data found for this applicant.</div>
//                 )}
//             </Modal.Body>
//             <Modal.Footer className="bg-light">
//                 <Button variant="secondary" onClick={onHide}>Close Profile</Button>
//             </Modal.Footer>
//         </Modal>
//     );
// };

// // --- APPLICANT RESULTS MODAL (Personality Assessments) ---
// // --- APPLICANT RESULTS MODAL (Personality Assessments) ---
// const ApplicantModal = ({ show, onHide, applicant, jobapplications, setSuccessMessage, updateJobApplication }) => {
//     const chartRefs = useRef({});
//     const [personalityData, setPersonalityData] = useState([]);
//     const [personalityJobTitles, setPersonalityJobTitles] = useState([]);
//     const [allSubmittedAnswers, setAllSubmittedAnswers] = useState([]);
//     const [filteredSubmittedAnswers, setFilteredSubmittedAnswers] = useState(null);
//     const [currentJobId, setCurrentJobId] = useState(null);
//     const [jobQuestions, setJobQuestions] = useState([]);
//     const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);
//     const [isLoadingPersonality, setIsLoadingPersonality] = useState(false);

//     useEffect(() => {
//         return () => {
//             Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//             chartRefs.current = {};
//         };
//     }, []);

//     useEffect(() => {
//         if (show && applicant?.id) {
//             fetchPersonalityData(applicant.id);
//         }
//     }, [show, applicant]);
    
//     useEffect(() => {
//         Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//         chartRefs.current = {};

//         if (Array.isArray(personalityData) && personalityData.length > 0) {
//             personalityData.forEach((data, index) => {
//                 const chartId = `personalityChart-${applicant?.id}-${index}`;
//                 const chartElement = document.getElementById(chartId);
//                 if (chartElement && data?.job_id) {
//                     const newChart = createDonutChart(data, chartId);
//                     if (newChart) chartRefs.current[index] = newChart;
//                 }
//             });
//         }
//     }, [personalityData, applicant?.id]);

//     useEffect(() => {
//         if (allSubmittedAnswers.length > 0 && currentJobId !== null) {
//             const filtered = allSubmittedAnswers.find(item => item.job_id === currentJobId || item.posting_id === currentJobId);
//             setFilteredSubmittedAnswers(filtered || null);
//         } else {
//             setFilteredSubmittedAnswers(null);
//         }
//     }, [allSubmittedAnswers, currentJobId]);

//     const handleStatusChange = async (id, newStatus) => {
//         try {
//             await axios.put(`https://raasbackend.vetrinahealthcare.com/applicants/update_status/${id}/`, { 
//                 job_status: newStatus 
//             });
            
//             setSuccessMessage("Job status updated successfully");
//             updateJobApplication(id, newStatus);
            
//             setTimeout(() => setSuccessMessage(""), 5000);
//         } catch (error) {
//             console.error("Error updating job status:", error);
//             alert("Failed to update job status. Please try again.");
//         }
//     };

//     const fetchPersonalityData = async (applicantId) => {
//         if (!applicantId) return;
        
//         setIsLoadingPersonality(true);
//         try {
//             const personalityResponse = await axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`);
            
//             const responseData = personalityResponse.data;
//             let fetchedPersonalityData = [];
            
//             if (responseData && responseData.results) {
//                 fetchedPersonalityData = responseData.results || [];
//             } else if (Array.isArray(responseData)) {
//                 fetchedPersonalityData = responseData;
//             }
            
//             setPersonalityData(fetchedPersonalityData);

//             if (fetchedPersonalityData.length > 0) {
//                 const jobTitles = fetchedPersonalityData.map(data => {
//                     if (data.job_title) return data.job_title;
//                     else if (data.opening_details?.jobtitle) return data.opening_details.jobtitle;
//                     else return `Job ID: ${data.job_id || data.posting_id}`;
//                 });
//                 setPersonalityJobTitles(jobTitles);
//             }
//         } catch (error) {
//             console.error("Error fetching personality data:", error);
//         } finally {
//             setIsLoadingPersonality(false);
//         }
//     };
    
//     const handleShowSubmittedAnswers = async (applicantId, jobId) => {
//         setCurrentJobId(jobId);
//         setIsLoadingAnswers(true);
//         try {
//             const answersResponse = await axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`);
            
//             let answersData = [];
//             const answersResponseData = answersResponse.data;
            
//             if (answersResponseData && answersResponseData.results) {
//                 answersData = answersResponseData.results || [];
//             } else if (Array.isArray(answersResponseData)) {
//                 answersData = answersResponseData;
//             }
            
//             setAllSubmittedAnswers(answersData);
            
//             const currentAssessment = answersData.find(item => 
//                 item.job_id === jobId || item.posting_id === jobId
//             );
            
//             if (!currentAssessment) {
//                 setJobQuestions([]);
//                 setIsLoadingAnswers(false);
//                 return;
//             }
            
//             const openingPost = currentAssessment.opening_post;
            
//             if (!openingPost) {
//                 setJobQuestions([]);
//                 setIsLoadingAnswers(false);
//                 return;
//             }
            
//             const questionResponse = await axios.get(
//                 `https://raasbackend.vetrinahealthcare.com/questions/${openingPost}/${jobId}/?applicant_id=${applicantId}`
//             );
            
//             const questionsData = questionResponse.data;
//             if (questionsData && questionsData.questions) {
//                 setJobQuestions(questionsData.questions || []);
//             } else if (Array.isArray(questionsData)) {
//                 setJobQuestions(questionsData);
//             } else {
//                 setJobQuestions([]);
//             }
            
//         } catch (error) {
//             console.error("Error fetching data:", error);
//             setJobQuestions([]);
//         } finally {
//             setIsLoadingAnswers(false);
//         }
//     };
    
//     const createDonutChart = (data, chartId) => {
//         const ctx = document.getElementById(chartId)?.getContext("2d");
//         if (!ctx || !data) return null;
        
//         const correctCount = data.correct_count || 0;
//         const wrongCount = data.wrong_count || 0;
//         const total = correctCount + wrongCount;
        
//         if (total === 0) return null;

//         return new Chart(ctx, {
//             type: 'doughnut',
//             data: {
//                 labels: [`Correct (${correctCount})`, `Wrong (${wrongCount})`],
//                 datasets: [{
//                     data: [correctCount, wrongCount],
//                     backgroundColor: ['rgba(40, 167, 69, 0.8)', 'rgba(220, 53, 69, 0.8)'],
//                     borderColor: ['rgba(40, 167, 69, 1)', 'rgba(220, 53, 69, 1)'],
//                     borderWidth: 1
//                 }]
//             },
//             options: {
//                 responsive: true,
//                 maintainAspectRatio: false,
//                 cutout: '60%',
//                 plugins: { legend: { display: false } }
//             }
//         });
//     };

//     // Simple function to render answer summary
// // Simple function to render answer summary
// const renderAnswerSummary = (question, index) => {
//     const userAnswer = question.user_selected_answer;
    
//     if (!userAnswer) {
//         return (
//             <div className="text-center p-3">
//                 <Badge bg="secondary">No answer data available</Badge>
//             </div>
//         );
//     }

//     const selectedIndex = userAnswer.selected_index;
//     const isCorrect = userAnswer.is_correct === "correct";
//     const correctIndex = question.correct_answer_index;
//     const options = question.answers;

//     return (
//         <div className="p-2">
//             {/* Simple Options Display */}
//             <div className="mb-1">
//                 <small className="text-muted fw-bold">Options:</small>
//             </div>
//             <div className="border rounded p-2">
//                 {options.map((option, idx) => {
//                     const isSelected = idx === selectedIndex;
//                     const isCorrectOpt = idx === correctIndex;
                    
//                     return (
//                         <div key={idx} className={`d-flex align-items-center p-2 mb-1 rounded ${isSelected && isCorrectOpt ? 'bg-success bg-opacity-25 border border-success' : isSelected ? 'bg-danger bg-opacity-25 border border-danger' : isCorrectOpt ? 'bg-success bg-opacity-10 border border-success' : ''}`}>
//                             <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${isSelected ? (isCorrectOpt ? 'bg-success text-white' : 'bg-danger text-white') : isCorrectOpt ? 'bg-success text-white' : 'bg-light text-dark border'}`}
//                                  style={{ width: '24px', height: '24px', fontSize: '12px', flexShrink: 0 }}>
//                                 {String.fromCharCode(65 + idx)}
//                             </div>
//                             <div className="flex-grow-1">
//                                 <span>{option}</span>
//                             </div>
//                             <div className="ms-2">
//                                 {isSelected && isCorrectOpt && <Badge bg="success">✓ Your Answer (Correct)</Badge>}
//                                 {isSelected && !isCorrectOpt && <Badge bg="danger">✗ Your Answer (Wrong)</Badge>}
//                                 {!isSelected && isCorrectOpt && <Badge bg="success">✓ Correct Answer</Badge>}
//                             </div>
//                         </div>
//                     );
//                 })}
//             </div>
            
//             {/* Simple Summary */}
//             <div className="mt-3 pt-2 border-top">
//                 <div className="row small">
//                     <div className="col-6 text-start">
//                         <span className="text-muted">Selected:</span>
//                         <div className={`d-inline-block ms-2 fw-bold ${isCorrect ? 'text-success' : 'text-danger'}`}>
//                             Option {String.fromCharCode(65 + selectedIndex)}
//                         </div>
//                     </div>
//                     <div className="col-6 text-end">
//                         <span className="text-muted">Correct:</span>
//                         <div className="d-inline-block ms-2 fw-bold text-success">
//                             Option {String.fromCharCode(65 + correctIndex)}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

//     return (
//         <Modal show={show} onHide={onHide} dialogClassName="modal-xl" centered scrollable>
//             <Modal.Header closeButton>
//                 <Modal.Title className="gradient-text">
//                     {applicant?.first_name || 'Applicant'} Assessment Results
//                 </Modal.Title>
//             </Modal.Header>
//             <Modal.Body className="p-4">
//                 {/* Job Applications Section */}
//                 <Card className="mb-4 shadow-sm">
//                     <Card.Header className="bg-light">
//                         <h5 className="mb-0">Job Applications</h5>
//                     </Card.Header>
//                     <Card.Body className="p-0">
//                         <Table striped bordered hover responsive className="mb-0">
//                             <thead className="table-light">
//                                 <tr>
//                                     <th>Job Title</th>
//                                     <th>Status</th>
//                                     <th>Applied On</th>
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {jobapplications.length > 0 ? (
//                                     jobapplications.map(jobApp => {
//                                         const statusOption = jobStatusOptions.find(opt => opt.value === jobApp.job_status);
                                        
//                                         return (
//                                             <tr key={jobApp.id}>
//                                                 <td>{jobApp.job_title || 'N/A'}</td>
//                                                 <td>
//                                                     <Form.Select 
//                                                         size="sm" 
//                                                         className={statusOption?.bgColor || ''}
//                                                         value={jobApp.job_status} 
//                                                         onChange={(e) => handleStatusChange(jobApp.id, e.target.value)}
//                                                         style={{ 
//                                                             color: statusOption?.bgColor.includes('text-dark') ? '#000' : '#fff',
//                                                             minWidth: '150px'
//                                                         }}
//                                                     >
//                                                         {jobStatusOptions.map(option => (
//                                                             <option key={option.value} value={option.value}>
//                                                                 {option.label}
//                                                             </option>
//                                                         ))}
//                                                     </Form.Select>
//                                                 </td>
//                                                 <td>
//                                                     {jobApp.applied_date ? 
//                                                         new Date(jobApp.applied_date).toLocaleDateString('en-GB') : 
//                                                         'N/A'
//                                                     }
//                                                 </td>
//                                             </tr>
//                                         );
//                                     })
//                                 ) : (
//                                     <tr>
//                                         <td colSpan="3" className="text-center p-3 text-muted">
//                                             No job applications found.
//                                         </td>
//                                     </tr>
//                                 )}
//                             </tbody>
//                         </Table>
//                     </Card.Body>
//                 </Card>

//                 {/* Personality Assessments Section */}
//                 <h5 className="gradient-text mt-4 mb-3">Personality Assessments</h5>
                
//                 {isLoadingPersonality ? (
//                     <div className="text-center p-4">
//                         <Spinner animation="border" variant="primary" />
//                         <p className="mt-2">Loading assessment data...</p>
//                     </div>
//                 ) : personalityData.length > 0 ? (
//                     <div className="d-flex flex-wrap justify-content-center gap-3">
//                         {personalityData.map((data, index) => {
//                             const jobId = data.job_id || data.posting_id;
//                             const jobTitle = personalityJobTitles[index] || `Assessment ${index + 1}`;
                            
//                             return data && jobId ? (
//                                 <Card key={`${jobId}-${index}`} className="text-center shadow-sm border-0" style={{ minWidth: '240px', flex: '1 1 240px' }}>
//                                     <Card.Header className="bg-primary text-white">
//                                         <Card.Title as="h6" className="mb-0 text-truncate" title={jobTitle}>
//                                             {jobTitle}
//                                         </Card.Title>
//                                     </Card.Header>
//                                     <Card.Body className="p-3">
//                                         <div style={{ height: '120px', width: '100%' }}>
//                                             <canvas id={`personalityChart-${applicant?.id}-${index}`}></canvas>
//                                         </div>
//                                         <div className="mt-3">
//                                             <h4 className="gradient-text fw-bold mb-2">
//                                                 {data.status ? `${data.status.toFixed(1)}%` : 'N/A'}
//                                             </h4>
//                                             <div className="d-flex justify-content-center gap-3 small text-muted">
//                                                 <span className="text-success">
//                                                     <i className="fas fa-check-circle"></i> {data.correct_count || 0} Correct
//                                                 </span>
//                                                 <span className="text-danger">
//                                                     <i className="fas fa-times-circle"></i> {data.wrong_count || 0} Wrong
//                                                 </span>
//                                             </div>
//                                         </div>
//                                         <Button 
//                                             variant="outline-primary" 
//                                             size="sm" 
//                                             className="mt-3"
//                                             onClick={() => handleShowSubmittedAnswers(applicant.id, jobId)}
//                                             disabled={isLoadingAnswers && currentJobId === jobId}
//                                         >
//                                             {isLoadingAnswers && currentJobId === jobId ? (
//                                                 <>
//                                                     <Spinner size="sm" animation="border" className="me-2" />
//                                                     Loading...
//                                                 </>
//                                             ) : (
//                                                 <>
//                                                     <i className="fas fa-eye me-2"></i>
//                                                     View Answers
//                                                 </>
//                                             )}
//                                         </Button>
//                                     </Card.Body>
//                                 </Card>
//                             ) : null;
//                         })}
//                     </div>
//                 ) : (
//                     <div className="text-center p-4 border rounded bg-light">
//                         <i className="fas fa-chart-pie fa-2x text-muted mb-3"></i>
//                         <p className="text-muted mb-0">No assessment data available.</p>
//                     </div>
//                 )}

//                 {/* Submitted Answers Section */}
//                 {currentJobId && (
//                     <div className="mt-4">
//                         <Card className="shadow-sm border-0">
//                             <Card.Header className="bg-info text-white">
//                                 <div className="d-flex justify-content-between align-items-center">
//                                     <h5 className="mb-0">
//                                         <i className="fas fa-list-check me-2"></i>
//                                         Submitted Answers
//                                     </h5>
//                                     <Badge bg="light" text="dark">
//                                         Score: {filteredSubmittedAnswers?.status || 0}%
//                                     </Badge>
//                                 </div>
//                             </Card.Header>
//                             <Card.Body>
//                                 {isLoadingAnswers ? (
//                                     <div className="text-center p-4">
//                                         <Spinner animation="border" variant="primary" />
//                                         <p className="mt-2">Loading answers...</p>
//                                     </div>
//                                 ) : jobQuestions.length > 0 ? (
//                                     <div className="row g-3">
//                                         {jobQuestions.map((question, index) => {
//                                             const userAnswer = question.user_selected_answer;
//                                             const isCorrect = userAnswer?.is_correct === "correct";
                                            
//                                             return (
//                                                 <div key={question.id || index} className="col-12">
//                                                     <Card className="border">
//                                                         <Card.Body>
//                                                             <div className="d-flex justify-content-between align-items-start mb-3">
//                                                                 <div>
//                                                                     <Badge bg="secondary" className="me-2">Q{index + 1}</Badge>
//                                                                     <span className="fw-bold">{question.question_text}</span>
//                                                                 </div>
//                                                                 <Badge bg={isCorrect ? "success" : "danger"} className="fs-6 px-3 py-2">
//                                                                     {isCorrect ? "✓ Correct" : "✗ Wrong"}
//                                                                 </Badge>
//                                                             </div>
                                                            
//                                                             {renderAnswerSummary(question, index)}
//                                                         </Card.Body>
//                                                     </Card>
//                                                 </div>
//                                             );
//                                         })}
//                                     </div>
//                                 ) : filteredSubmittedAnswers ? (
//                                     // Simple summary when no questions
//                                     <Card className="border-0 bg-light">
//                                         <Card.Body className="text-center">
//                                             <div className="d-flex justify-content-center gap-4 mb-3">
//                                                 <div className="text-center">
//                                                     <div className="display-6 text-success">{filteredSubmittedAnswers.correct_count || 0}</div>
//                                                     <small className="text-muted">Correct</small>
//                                                 </div>
//                                                 <div className="text-center">
//                                                     <div className="display-6 text-danger">{filteredSubmittedAnswers.wrong_count || 0}</div>
//                                                     <small className="text-muted">Wrong</small>
//                                                 </div>
//                                                 <div className="text-center">
//                                                     <div className="display-6 text-primary">{filteredSubmittedAnswers.all_count || 0}</div>
//                                                     <small className="text-muted">Total</small>
//                                                 </div>
//                                             </div>
//                                             <p className="text-muted mb-0">
//                                                 Detailed question data not available
//                                             </p>
//                                         </Card.Body>
//                                     </Card>
//                                 ) : (
//                                     <div className="text-center p-4">
//                                         <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
//                                         <p className="text-muted mb-0">No answers found for this assessment.</p>
//                                     </div>
//                                 )}
//                             </Card.Body>
//                         </Card>
//                     </div>
//                 )}
//             </Modal.Body>
//             <Modal.Footer className="bg-light">
//                 <Button variant="secondary" onClick={onHide}>
//                     <i className="fas fa-times me-2"></i>
//                     Close
//                 </Button>
//             </Modal.Footer>
//         </Modal>
//     );
// };



// // --- MAIN INTERVIEWER DASHBOARD COMPONENT ---
// function InterviewerDashboard() {
//     const [interviewSchedule, setInterviewSchedule] = useState([]);
//     const [currentPage, setCurrentPage] = useState(1);
//     const itemsPerPage = 5;
//     const [searchQuery, setSearchQuery] = useState("");
//     const [loadingTable, setLoadingTable] = useState(true);
//     const [showProfileModal, setShowProfileModal] = useState(false);
//     const [showResultsModal, setShowResultsModal] = useState(false);
//     const [selectedApplicant, setSelectedApplicant] = useState(null);
//     const [loadingView, setLoadingView] = useState({});
//     const [loadingResume, setLoadingResume] = useState({});
//     const [selectedApplicantForResults, setSelectedApplicantForResults] = useState(null);
//     const [jobApplicationsForModal, setJobApplicationsForModal] = useState([]);
//     const [loadingResults, setLoadingResults] = useState({});
//     const [successMessage, setSuccessMessage] = useState("");

//     useEffect(() => { 
//         fetchInterviewSchedule(); 
//     }, []);

//     const fetchInterviewSchedule = () => {
//         const userEmail = localStorage.getItem("email");
//         if (!userEmail) {
//             setLoadingTable(false);
//             return;
//         }
//         setLoadingTable(true);
//         axios.get("https://raasbackend.vetrinahealthcare.com/interviews/")
//             .then(response => {
//                 const allInterviews = response.data;
//                 const userInterviews = allInterviews.filter(interview => interview.interviewer_email === userEmail);
                
//                 const formattedData = userInterviews.map(interview => ({
//                     ...interview,
//                     date: formatDate(interview.date),
//                     time: formatTime(interview.time)
//                 }));
                
//                 setInterviewSchedule(formattedData);
//             })
//             .catch(error => console.error("Error fetching schedule:", error))
//             .finally(() => setLoadingTable(false));
//     };

//     const handleViewResultsClick = async (interview) => {
//         const email = interview.candidate_email;
//         if (!email) return;
        
//         setLoadingResults(prev => ({ ...prev, [email]: true }));
//         try {
//             const applicantRes = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
            
//             if (!applicantRes.data || !applicantRes.data.id) {
//                 alert("Applicant data not found");
//                 return;
//             }
            
//             setSelectedApplicantForResults(applicantRes.data);
            
//             // Fetch job applications for this applicant
//             const jobsRes = await axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantRes.data.id}/`);
//             setJobApplicationsForModal(jobsRes.data || []);
            
//             // Show the results modal
//             setShowResultsModal(true);
//         } catch (error) {
//             console.error("Error fetching data for results modal:", error);
//             alert("Failed to load applicant results. Please try again.");
//         } finally {
//             setLoadingResults(prev => ({ ...prev, [email]: false }));
//         }
//     };
    
//     const updateJobApplicationStatusInList = (jobAppId, newStatus) => {
//         setJobApplicationsForModal(prev => 
//             prev.map(app => app.id === jobAppId ? { ...app, job_status: newStatus } : app)
//         );
//     };

//     const formatDate = (dateString) => {
//         if (!dateString) return 'N/A';
//         try {
//             return new Date(dateString).toLocaleDateString('en-GB');
//         } catch (e) {
//             return dateString;
//         }
//     };
    
//     const formatTime = (timeString) => {
//         if (!timeString) return 'N/A';
//         try {
//             const [hours, minutes] = timeString.split(':');
//             const hour = parseInt(hours, 10);
//             const ampm = hour >= 12 ? 'PM' : 'AM';
//             const hour12 = hour % 12 || 12;
//             return `${hour12}:${minutes} ${ampm}`;
//         } catch (e) {
//             return timeString;
//         }
//     };

//     const handleViewClick = async (email) => {
//         setLoadingView(prev => ({ ...prev, [email]: true }));
//         try {
//             const response = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
            
//             if (response.data) {
//                 setSelectedApplicant(response.data);
//                 setShowProfileModal(true);
//             } else {
//                 alert("No applicant data found");
//             }
//         } catch (error) {
//             console.error("Error fetching applicant data:", error);
//             alert("Failed to load applicant details. Please try again.");
//         } finally {
//             setLoadingView(prev => ({ ...prev, [email]: false }));
//         }
//     };

//     const handleViewResume = async (email) => {
//         setLoadingResume(prev => ({ ...prev, [email]: true }));
//         try {
//             const response = await axios.post(
//                 "https://raasbackend.vetrinahealthcare.com/view_resume/", 
//                 { email },
//                 { responseType: 'blob' }
//             );
            
//             const blob = new Blob([response.data], { type: 'application/pdf' });
//             const url = window.URL.createObjectURL(blob);
//             window.open(url, '_blank');
//             window.URL.revokeObjectURL(url);
//         } catch (error) {
//             console.error("Error fetching resume:", error);
//             alert("Failed to load resume. Please try again.");
//         } finally {
//             setLoadingResume(prev => ({ ...prev, [email]: false }));
//         }
//     };

//     const filteredSchedule = interviewSchedule.filter(i => 
//         Object.values(i).some(val => 
//             String(val).toLowerCase().includes(searchQuery.toLowerCase())
//         )
//     );
    
//     const currentItems = filteredSchedule.slice(
//         (currentPage - 1) * itemsPerPage, 
//         currentPage * itemsPerPage
//     );
    
//     const totalPages = Math.ceil(filteredSchedule.length / itemsPerPage);

//     return (
//         <>
//             <DashNavbar />
//             <div className="wrapper">
//                 <Sidebar />
//                 <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//                     <div className="container mt-4">
//                         {successMessage && (
//                             <Alert variant="success" onClose={() => setSuccessMessage("")} dismissible>
//                                 {successMessage}
//                             </Alert>
//                         )}
                        
//                         <div className="text-start">
//                             <h3>Welcome to your Dashboard</h3>
//                             <p>Below is your interview schedule:</p>
//                         </div>
                        
//                         <div className="auth-inner mt-5 w-100" style={{ padding: "10px" }}>
//                             <h2 className="mb-4">Interview Schedule</h2>
                            
//                             <div className="row mb-3">
//                                 <div className="col-12 col-md-6 mx-auto">
//                                     <InputGroup>
//                                         <Form.Control 
//                                             type="search" 
//                                             placeholder="Search by name, email, job title..." 
//                                             value={searchQuery} 
//                                             onChange={e => setSearchQuery(e.target.value)} 
//                                         />
//                                     </InputGroup>
//                                 </div>
//                             </div>
                            
//                             <div className="table-responsive">
//                                 <Table striped bordered hover size="sm">
//                                     <thead className="thead-dark">
//                                         <tr>
//                                             <th>Sr.No</th>
//                                             <th>Candidate</th>
//                                             <th>Date</th>
//                                             <th>Time</th>
//                                             <th>Applied Job</th>
//                                             <th>Opportunity Type</th>
//                                             <th>Meeting</th>
//                                             <th>Actions</th>
//                                         </tr>
//                                     </thead>
//                                     <tbody>
//                                         {loadingTable ? (
//                                             <tr>
//                                                 <td colSpan="8" className="text-center p-4">
//                                                     <Spinner animation="border" variant="primary" />
//                                                     <p className="mt-2">Loading schedule...</p>
//                                                 </td>
//                                             </tr>
//                                         ) : currentItems.length > 0 ? (
//                                             currentItems.map((interview, index) => (
//                                                 <tr key={interview.id || index}>
//                                                     <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                                                     <td>{interview.candidate}</td>
//                                                     <td>{interview.date}</td>
//                                                     <td>{interview.time}</td>
//                                                     <td>{interview.job_title || 'N/A'}</td>
//                                                     <td>{formatOpportunityType(interview.opening_post)}</td>
//                                                     <td>
//                                                         {interview.meetLink ? (
//                                                             <a 
//                                                                 href={interview.meetLink} 
//                                                                 target="_blank" 
//                                                                 rel="noopener noreferrer" 
//                                                                 className="btn btn-primary btn-sm"
//                                                             >
//                                                                 Join Meeting
//                                                             </a>
//                                                         ) : (
//                                                             <span>{interview.mode || 'Offline'}</span>
//                                                         )}
//                                                     </td>
//                                                     <td>
//                                                         <div className="d-flex flex-wrap gap-2">
//                                                             <Button 
//                                                                 variant="secondary" 
//                                                                 size="sm" 
//                                                                 onClick={() => handleViewClick(interview.candidate_email)} 
//                                                                 disabled={loadingView[interview.candidate_email]}
//                                                             >
//                                                                 {loadingView[interview.candidate_email] ? (
//                                                                     <Spinner size="sm" animation="border" />
//                                                                 ) : "View Profile"}
//                                                             </Button>
                                                            
//                                                             <Button 
//                                                                 variant="info" 
//                                                                 size="sm" 
//                                                                 className="text-white"
//                                                                 onClick={() => handleViewResultsClick(interview)} 
//                                                                 disabled={loadingResults[interview.candidate_email]}
//                                                             >
//                                                                 {loadingResults[interview.candidate_email] ? (
//                                                                     <Spinner size="sm" animation="border" />
//                                                                 ) : "Results"}
//                                                             </Button>
                                                            
//                                                             <Button 
//                                                                 variant="secondary" 
//                                                                 size="sm" 
//                                                                 onClick={() => handleViewResume(interview.candidate_email)} 
//                                                                 disabled={loadingResume[interview.candidate_email]}
//                                                             >
//                                                                 {loadingResume[interview.candidate_email] ? (
//                                                                     <Spinner size="sm" animation="border" />
//                                                                 ) : "Resume"}
//                                                             </Button>
//                                                         </div>
//                                                     </td>
//                                                 </tr>
//                                             ))
//                                         ) : (
//                                             <tr>
//                                                 <td colSpan="8" className="text-center p-4">
//                                                     No interviews scheduled.
//                                                 </td>
//                                             </tr>
//                                         )}
//                                     </tbody>
//                                 </Table>
//                             </div>
                            
//                             {totalPages > 1 && (
//                                 <Pagination className="justify-content-center mt-4">
//                                     {[...Array(totalPages).keys()].map(n => (
//                                         <Pagination.Item 
//                                             key={n + 1} 
//                                             active={n + 1 === currentPage} 
//                                             onClick={() => setCurrentPage(n + 1)}
//                                         >
//                                             {n + 1}
//                                         </Pagination.Item>
//                                     ))}
//                                 </Pagination>
//                             )}
//                         </div>
//                     </div>
//                 </div>
//             </div>
            
//             {/* Profile Details Modal - opens when "View" button is clicked */}
//             <ApplicantDetailsModal 
//                 applicant={selectedApplicant || {}} 
//                 show={showProfileModal} 
//                 onHide={() => setShowProfileModal(false)} 
//             />
            
//             {/* Results Modal - opens when "Results" button is clicked */}
//             {showResultsModal && (
//                 <ApplicantModal 
//                     show={showResultsModal} 
//                     onHide={() => setShowResultsModal(false)} 
//                     applicant={selectedApplicantForResults} 
//                     jobapplications={jobApplicationsForModal} 
//                     setSuccessMessage={setSuccessMessage} 
//                     updateJobApplication={updateJobApplicationStatusInList} 
//                 />
//             )}
//         </>
//     );
// }

// export default InterviewerDashboard;

// import React, { useState, useEffect, useRef, useMemo } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination, Spinner, Button, Modal, Card, Table, Form, Alert, InputGroup } from "react-bootstrap";
// import axios from 'axios';
// import Chart from "chart.js/auto";

// // --- GLOBAL OPTIONS FOR MODAL ---
// // We define this here so the ApplicantModal below can access it.
// const jobStatusOptions = [
//     { value: "applied", label: "Applied", bgColor: "bg-info" },
//     { value: "under_review", label: "Under Review", bgColor: "bg-secondary" },
//     { value: "interview_scheduled", label: "Interview Scheduled", bgColor: "bg-warning text-dark" },
//     { value: "selected", label: "Selected", bgColor: "bg-success" },
//     { value: "rejected", label: "Rejected", bgColor: "bg-danger" },
// ];


// // --- APPLICANT MODAL COMPONENT (DEFINED IN-FILE) ---
// // By defining this component here, we don't need to create a separate file for it.
// const ApplicantModal = ({ show, onHide, applicant, jobapplications, setSuccessMessage, updateJobApplication }) => {
//     const chartRefs = useRef({});
//     const [personalityData, setPersonalityData] = useState([]);
//     const [personalityJobTitles, setPersonalityJobTitles] = useState([]);
//     const [allSubmittedAnswers, setAllSubmittedAnswers] = useState(null);
//     const [filteredSubmittedAnswers, setFilteredSubmittedAnswers] = useState(null);
//     const [currentJobId, setCurrentJobId] = useState(null);
//     const [jobQuestions, setJobQuestions] = useState(null);
//     const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);

//     useEffect(() => {
//         // Cleanup when modal closes or applicant changes
//         return () => {
//             Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//             chartRefs.current = {};
//         };
//     }, [applicant]);

//     useEffect(() => {
//         if (show && applicant?.id) {
//             fetchPersonalityData(applicant.id);
//         }
//     }, [show, applicant]);
    
//     useEffect(() => {
//         // Redraw charts when data is available
//         Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//         chartRefs.current = {};

//         if (Array.isArray(personalityData)) {
//             personalityData.forEach((data, index) => {
//                 const chartId = `personalityChart-${applicant?.id}-${index}`;
//                 const chartElement = document.getElementById(chartId);
//                 if (chartElement && data?.job_id) {
//                     const newChart = createDonutChart(data, chartId);
//                     if (newChart) chartRefs.current[index] = newChart;
//                 }
//             });
//         }
//     }, [personalityData, applicant?.id]);

//     useEffect(() => {
//         if (allSubmittedAnswers && currentJobId !== null) {
//             const filtered = allSubmittedAnswers.find(item => item.job_id === currentJobId);
//             setFilteredSubmittedAnswers(filtered || null);
//         } else {
//             setFilteredSubmittedAnswers(null);
//         }
//     }, [allSubmittedAnswers, currentJobId]);
    
//     const handleStatusChange = (id, newStatus) => {
//         axios.put(`https://raasbackend.vetrinahealthcare.com/applicants/update_status/${id}/`, { job_status: newStatus })
//             .then(() => {
//                 setSuccessMessage("Job status updated successfully");
//                 updateJobApplication(id, newStatus);
//                 setTimeout(() => setSuccessMessage(""), 5000);
//             }).catch(error => console.error("Error updating job status:", error));
//     };

//     const fetchPersonalityData = async (applicantId) => {
//         try {
//             const [personalityResponse, jobTitleResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantId}/`)
//             ]);
            
//             const fetchedPersonalityData = personalityResponse.data || [];
//             setPersonalityData(fetchedPersonalityData);

//             if (fetchedPersonalityData.length > 0) {
//                 const jobTitlesMap = (jobTitleResponse.data || []).reduce((acc, job) => {
//                     if (job.job_id) acc[job.job_id] = job.job_title;
//                     return acc;
//                 }, {});
//                 const jobTitles = fetchedPersonalityData.map(data => jobTitlesMap[data.job_id] || `Job ID: ${data.job_id}`);
//                 setPersonalityJobTitles(jobTitles);
//             }
//         } catch (error) {
//             console.error("Error fetching personality data:", error);
//         }
//     };
    
//     const handleShowSubmittedAnswers = async (applicantId, jobId) => {
//         setCurrentJobId(jobId);
//         setIsLoadingAnswers(true);
//         try {
//             const [answersResponse, questionResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/questions/${jobId}/`)
//             ]);
//             setAllSubmittedAnswers(answersResponse.data);
//             setJobQuestions(questionResponse.data || []);
//         } catch (error) {
//             console.error("Error fetching answers:", error);
//         } finally {
//             setIsLoadingAnswers(false);
//         }
//     };
    
//     const createDonutChart = (data, chartId) => {
//         const ctx = document.getElementById(chartId)?.getContext("2d");
//         if (!ctx || !data || typeof data.correct_count !== 'number' || typeof data.wrong_count !== 'number') return null;
        
//         return new Chart(ctx, {
//             type: 'doughnut',
//             data: {
//                 labels: [`Correct (${data.correct_count})`, `Wrong (${data.wrong_count})`],
//                 datasets: [{
//                     data: [data.correct_count, data.wrong_count],
//                     backgroundColor: ['rgba(40, 167, 69, 0.7)', 'rgba(220, 53, 69, 0.7)'],
//                 }]
//             },
//             options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { display: false } } }
//         });
//     };

//     return (
//         <Modal show={show} onHide={onHide} dialogClassName="modal-xl" centered scrollable>
//             <Modal.Header closeButton><Modal.Title className="gradient-text">{applicant?.first_name || 'Applicant'} Details</Modal.Title></Modal.Header>
//             <Modal.Body className="p-4">
//                 <Card className="mb-4 shadow-sm"><Card.Header><h5 className="mb-0">Job Applications</h5></Card.Header>
//                     <Card.Body className="p-0">
//                         <Table striped bordered hover responsive size="sm" className="mb-0">
//                             <thead><tr><th>Job Title</th><th>Status</th><th>Applied On</th></tr></thead>
//                             <tbody>
//                                 {jobapplications.length > 0 ? jobapplications.map(jobApp => {
//                                     const statusOption = jobStatusOptions.find(opt => opt.value === jobApp.job_status);
//                                     return (
//                                         <tr key={jobApp.id}>
//                                             <td>{jobApp.job_title || 'N/A'}</td>
//                                             <td>
//                                                 <Form.Select size="sm" className={`${statusOption?.bgColor || ''}`} value={jobApp.job_status} onChange={(e) => handleStatusChange(jobApp.id, e.target.value)} style={{ color: statusOption?.bgColor.includes('text-dark') ? '#000' : '#fff' }}>
//                                                     {jobStatusOptions.map(option => <option key={option.value} value={option.value} style={{ backgroundColor: '#fff', color: '#000' }}>{option.label}</option>)}
//                                                 </Form.Select>
//                                             </td>
//                                             <td>{jobApp.applied_date ? new Date(jobApp.applied_date).toLocaleDateString() : 'N/A'}</td>
//                                         </tr>
//                                     );
//                                 }) : <tr><td colSpan="3" className="text-center p-3">No job applications found.</td></tr>}
//                             </tbody>
//                         </Table>
//                     </Card.Body>
//                 </Card>
//                 <h4 className="gradient-text text-center mt-4 mb-3">Personality Assessments</h4>
//                 <div className="d-flex flex-wrap justify-content-center gap-3">
//                     {personalityData.length > 0 ? personalityData.map((data, index) => (
//                         data && data.job_id ? (
//                             <Card key={`${data.job_id}-${index}`} className="text-center shadow-sm" style={{ minWidth: '260px', flex: '1 1 260px' }}>
//                                 <Card.Header><Card.Title as="h6" className="mb-0 text-truncate">{personalityJobTitles[index] || `Assessment ID: ${data.job_id}`}</Card.Title></Card.Header>
//                                 <Card.Body className="p-2">
//                                     <div style={{ height: '150px', width: '100%' }}><canvas id={`personalityChart-${applicant?.id}-${index}`}></canvas></div>
//                                     <p className="mb-1"><strong>Score:</strong> <b className="gradient-text">{typeof data.status === 'number' ? `${data.status.toFixed(1)}%` : 'N/A'}</b></p>
//                                     <Button variant="outline-info" size="sm" onClick={() => handleShowSubmittedAnswers(applicant.id, data.job_id)} disabled={isLoadingAnswers && currentJobId === data.job_id}>
//                                         {isLoadingAnswers && currentJobId === data.job_id ? <Spinner as="span" size="sm" /> : "View Answers"}
//                                     </Button>
//                                 </Card.Body>
//                             </Card>
//                         ) : null
//                     )) : <p className="text-center text-muted">No assessment data available.</p>}
//                 </div>
//                 {currentJobId && (
//                     <div className="mt-4"><Card className="shadow-sm"><Card.Header>
//                         <h5 className="mb-0">Submitted Answers for Job: {personalityJobTitles[personalityData.findIndex(p => p.job_id === currentJobId)] || `ID ${currentJobId}`}</h5>
//                     </Card.Header><Card.Body>
//                         {isLoadingAnswers ? <div className="text-center p-4"><Spinner /></div> : 
//                         filteredSubmittedAnswers && jobQuestions ? (
//                             <Table striped bordered responsive size="sm">
//                                 <thead><tr><th>#</th><th>Question</th><th>Your Answer</th><th>Result</th></tr></thead>
//                                 <tbody>
//                                     {jobQuestions.map((q, i) => {
//                                         const subAns = filteredSubmittedAnswers.selected_answers?.find(a => a.question_id === q.id);
//                                         const isCorrect = subAns && subAns.selected_answer_index === q.correct_answer_index;
//                                         return (
//                                             <tr key={q.id}>
//                                                 <td>{i + 1}</td><td>{q.question_text}</td>
//                                                 <td>{subAns ? (q.answers[subAns.selected_answer_index] || subAns.selected_answer_text) : <i>Not Answered</i>}</td>
//                                                 <td className="text-center">{subAns ? <span className={`badge ${isCorrect ? 'bg-success' : 'bg-danger'}`}>{isCorrect ? 'Correct' : 'Wrong'}</span> : <span className="badge bg-secondary">N/A</span>}</td>
//                                             </tr>
//                                         );
//                                     })}
//                                 </tbody>
//                             </Table>
//                         ) : <p className="text-center p-3">Could not load answers.</p>}
//                     </Card.Body></Card></div>
//                 )}
//             </Modal.Body>
//             <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
//         </Modal>
//     );
// };


// // --- YOUR MAIN COMPONENT ---
// function InterviewerDashboard() {
//     const [interviewSchedule, setInterviewSchedule] = useState([]);
//     const [appliedJobs, setAppliedJobs] = useState({});
//     const [currentPage, setCurrentPage] = useState(1);
//     const itemsPerPage = 5;
//     const [searchQuery, setSearchQuery] = useState("");
//     const [loadingTable, setLoadingTable] = useState(true);
//     const [showModal, setShowModal] = useState(false);
//     const [selectedApplicant, setSelectedApplicant] = useState(null);
//     const [loadingView, setLoadingView] = useState({});
//     const [loadingResume, setLoadingResume] = useState({});

//     // State for the "Results" modal
//     const [showResultsModal, setShowResultsModal] = useState(false);
//     const [selectedApplicantForResults, setSelectedApplicantForResults] = useState(null);
//     const [jobApplicationsForModal, setJobApplicationsForModal] = useState([]);
//     const [loadingResults, setLoadingResults] = useState({});
//     const [successMessage, setSuccessMessage] = useState("");

//     useEffect(() => { fetchInterviewSchedule(); }, []);

//     const fetchInterviewSchedule = () => {
//         const userEmail = localStorage.getItem("email");
//         if (!userEmail) {
//             setLoadingTable(false);
//             return;
//         }
//         setLoadingTable(true);
//         // UPDATED: Fetch all interviews from the new GET endpoint
//         axios.get("https://raasbackend.vetrinahealthcare.com/interviews/")
//             .then(response => {
//                 const allInterviews = response.data;
//                 // Filter the interviews on the client-side for the logged-in interviewer
//                 const userInterviews = allInterviews.filter(interview => interview.interviewer_email === userEmail);
                
//                 const formattedData = userInterviews.map(interview => ({
//                     ...interview,
//                     date: formatDate(interview.date),
//                     time: formatTime(interview.time)
//                 }));
                
//                 setInterviewSchedule(formattedData);
//                 fetchAppliedJobsForSchedule(formattedData);
//             })
//             .catch(error => console.error("Error fetching schedule:", error))
//             .finally(() => setLoadingTable(false));
//     };

//     const fetchAppliedJobsForSchedule = (schedule) => {
//         schedule.forEach(interview => {
//             fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${interview.candidate_email}/`)
//                 .then(res => res.json())
//                 .then(data => setAppliedJobs(prev => ({ ...prev, [interview.candidate_email]: data.job_titles || ["N/A"] })))
//                 .catch(error => console.error("Error fetching applied jobs:", error));
//         });
//     };

//     const handleViewResultsClick = async (interview) => {
//         const email = interview.candidate_email;
//         if (!email) return;
//         setLoadingResults(prev => ({ ...prev, [email]: true }));
//         try {
//             const applicantRes = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
//             if (!applicantRes.data || !applicantRes.data.id) throw new Error("Applicant ID not found.");
//             setSelectedApplicantForResults(applicantRes.data);
//             const jobsRes = await axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantRes.data.id}/`);
//             setJobApplicationsForModal(jobsRes.data || []);
//             setShowResultsModal(true);
//         } catch (error) {
//             console.error("Error fetching data for results modal:", error);
//         } finally {
//             setLoadingResults(prev => ({ ...prev, [email]: false }));
//         }
//     };
    
//     const updateJobApplicationStatusInList = (jobAppId, newStatus) => {
//         setJobApplicationsForModal(prev => prev.map(app => app.id === jobAppId ? { ...app, job_status: newStatus } : app));
//     };

//     const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB');
//     const formatTime = (timeString) => new Date(`01/01/2022 ${timeString}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

//     const handleViewClick = (email) => {
//         setLoadingView({ ...loadingView, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.json()).then(data => { setSelectedApplicant(data); setShowModal(true); })
//             .catch(err => console.error("Error fetching applicant data:", err))
//             .finally(() => setLoadingView({ ...loadingView, [email]: false }));
//     };

//     const handleViewResume = (email) => {
//         setLoadingResume({ ...loadingResume, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.blob()).then(blob => window.open(URL.createObjectURL(blob), "_blank"))
//             .catch(err => console.error("Error fetching resume:", err))
//             .finally(() => setLoadingResume({ ...loadingResume, [email]: false }));
//     };

//     const filteredSchedule = interviewSchedule.filter(i => Object.values(i).some(val => String(val).toLowerCase().includes(searchQuery.toLowerCase())) || appliedJobs[i.candidate_email]?.some(j => j.toLowerCase().includes(searchQuery.toLowerCase())));
//     const currentItems = filteredSchedule.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
//     const totalPages = Math.ceil(filteredSchedule.length / itemsPerPage);

//     return (
//         <>
//             <DashNavbar />
//             <div className="wrapper">
//                 <Sidebar />
//                 <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//                     <div className="container mt-4">
//                         <div className="text-start"><h3>Welcome to your Dashboard</h3><p>Below is your interview schedule:</p></div>
//                         <div className="auth-inner mt-5 w-100" style={{ padding: "10px" }}>
//                             <h2 className="mb-4">Interview Schedule</h2>
//                             <div className="row mb-3"><div className="col-12 col-md-6 mx-auto"><InputGroup><Form.Control type="search" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></InputGroup></div></div>
//                             <div className="table-responsive">
//                                 <Table striped bordered hover size="sm">
//                                     <thead className="thead-dark"><tr><th>Sr.No</th><th>Candidate</th><th>Date</th><th>Time</th><th>Applied Job</th><th>Meeting</th><th>Actions</th></tr></thead>
//                                     <tbody>
//                                         {loadingTable ? (<tr><td colSpan="7" className="text-center p-4"><Spinner /></td></tr>)
//                                         : currentItems.length > 0 ? (currentItems.map((interview, index) => (
//                                             <tr key={interview.id || index}>
//                                                 <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                                                 <td>{interview.candidate}</td>
//                                                 <td>{interview.date}</td>
//                                                 <td>{interview.time}</td>
//                                                 <td>{interview.job_title || 'N/A'}</td> {/* Using job_title from interview data */}
//                                                 <td>
//                                                   {interview.meetLink ? (
//                                                     <a href={interview.meetLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">Join</a>
//                                                   ) : (
//                                                     <span>{interview.location || 'N/A'}</span> // Display location for offline interviews
//                                                   )}
//                                                 </td>
//                                                 <td>
//                                                     <div className="d-flex flex-sm-row flex-column">
//                                                         <Button variant="secondary" size="sm" className="me-sm-2 mb-2 mb-sm-0" onClick={() => handleViewClick(interview.candidate_email)} disabled={loadingView[interview.candidate_email]}>{loadingView[interview.candidate_email] ? <Spinner size="sm"/> : "View"}</Button>
//                                                         <Button variant="info" size="sm" className="me-sm-2 mb-2 mb-sm-0 text-white" onClick={() => handleViewResultsClick(interview)} disabled={loadingResults[interview.candidate_email]}>{loadingResults[interview.candidate_email] ? <Spinner size="sm"/> : "Results"}</Button>
//                                                         <Button variant="secondary" size="sm" onClick={() => handleViewResume(interview.candidate_email)} disabled={loadingResume[interview.candidate_email]}>{loadingResume[interview.candidate_email] ? <Spinner size="sm"/> : "Resume"}</Button>
//                                                     </div>
//                                                 </td>
//                                             </tr>
//                                         ))) : (<tr><td colSpan="7" className="text-center p-4">No interviews found.</td></tr>)}
//                                     </tbody>
//                                 </Table>
//                             </div>
//                             {totalPages > 1 && (<Pagination className="justify-content-center mt-4">{[...Array(totalPages).keys()].map(n=><Pagination.Item key={n+1} active={n+1===currentPage} onClick={()=>setCurrentPage(n+1)}>{n+1}</Pagination.Item>)}</Pagination>)}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//             <ApplicantDetailsModal applicant={selectedApplicant || {}} show={showModal} onHide={() => setShowModal(false)} />
//             {showResultsModal && <ApplicantModal show={showResultsModal} onHide={() => setShowResultsModal(false)} applicant={selectedApplicantForResults} jobapplications={jobApplicationsForModal} setSuccessMessage={setSuccessMessage} updateJobApplication={updateJobApplicationStatusInList} />}
//         </>
//     );
// }

// export default InterviewerDashboard;

// import React, { useState, useEffect, useRef, useMemo } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination, Spinner, Button, Modal, Card, Table, Form, Alert, InputGroup } from "react-bootstrap";
// import axios from 'axios';
// import Chart from "chart.js/auto";

// // --- GLOBAL OPTIONS FOR MODAL ---
// // We define this here so the ApplicantModal below can access it.
// const jobStatusOptions = [
//     { value: "applied", label: "Applied", bgColor: "bg-info" },
//     { value: "under_review", label: "Under Review", bgColor: "bg-secondary" },
//     { value: "interview_scheduled", label: "Interview Scheduled", bgColor: "bg-warning text-dark" },
//     { value: "selected", label: "Selected", bgColor: "bg-success" },
//     { value: "rejected", label: "Rejected", bgColor: "bg-danger" },
// ];


// // --- APPLICANT MODAL COMPONENT (DEFINED IN-FILE) ---
// // By defining this component here, we don't need to create a separate file for it.
// const ApplicantModal = ({ show, onHide, applicant, jobapplications, setSuccessMessage, updateJobApplication }) => {
//     const chartRefs = useRef({});
//     const [personalityData, setPersonalityData] = useState([]);
//     const [personalityJobTitles, setPersonalityJobTitles] = useState([]);
//     const [allSubmittedAnswers, setAllSubmittedAnswers] = useState(null);
//     const [filteredSubmittedAnswers, setFilteredSubmittedAnswers] = useState(null);
//     const [currentJobId, setCurrentJobId] = useState(null);
//     const [jobQuestions, setJobQuestions] = useState(null);
//     const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);

//     useEffect(() => {
//         // Cleanup when modal closes or applicant changes
//         return () => {
//             Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//             chartRefs.current = {};
//         };
//     }, [applicant]);

//     useEffect(() => {
//         if (show && applicant?.id) {
//             fetchPersonalityData(applicant.id);
//         }
//     }, [show, applicant]);
    
//     useEffect(() => {
//         // Redraw charts when data is available
//         Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//         chartRefs.current = {};

//         if (Array.isArray(personalityData)) {
//             personalityData.forEach((data, index) => {
//                 const chartId = `personalityChart-${applicant?.id}-${index}`;
//                 const chartElement = document.getElementById(chartId);
//                 if (chartElement && data?.job_id) {
//                     const newChart = createDonutChart(data, chartId);
//                     if (newChart) chartRefs.current[index] = newChart;
//                 }
//             });
//         }
//     }, [personalityData, applicant?.id]);

//     useEffect(() => {
//         if (allSubmittedAnswers && currentJobId !== null) {
//             const filtered = allSubmittedAnswers.find(item => item.job_id === currentJobId);
//             setFilteredSubmittedAnswers(filtered || null);
//         } else {
//             setFilteredSubmittedAnswers(null);
//         }
//     }, [allSubmittedAnswers, currentJobId]);
    
//     const handleStatusChange = (id, newStatus) => {
//         axios.put(`https://raasbackend.vetrinahealthcare.com/applicants/update_status/${id}/`, { job_status: newStatus })
//             .then(() => {
//                 setSuccessMessage("Job status updated successfully");
//                 updateJobApplication(id, newStatus);
//                 setTimeout(() => setSuccessMessage(""), 5000);
//             }).catch(error => console.error("Error updating job status:", error));
//     };

//     const fetchPersonalityData = async (applicantId) => {
//         try {
//             const [personalityResponse, jobTitleResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantId}/`)
//             ]);
            
//             const fetchedPersonalityData = personalityResponse.data || [];
//             setPersonalityData(fetchedPersonalityData);

//             if (fetchedPersonalityData.length > 0) {
//                 const jobTitlesMap = (jobTitleResponse.data || []).reduce((acc, job) => {
//                     if (job.job_id) acc[job.job_id] = job.job_title;
//                     return acc;
//                 }, {});
//                 const jobTitles = fetchedPersonalityData.map(data => jobTitlesMap[data.job_id] || `Job ID: ${data.job_id}`);
//                 setPersonalityJobTitles(jobTitles);
//             }
//         } catch (error) {
//             console.error("Error fetching personality data:", error);
//         }
//     };
    
//     const handleShowSubmittedAnswers = async (applicantId, jobId) => {
//         setCurrentJobId(jobId);
//         setIsLoadingAnswers(true);
//         try {
//             const [answersResponse, questionResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/questions/${jobId}/`)
//             ]);
//             setAllSubmittedAnswers(answersResponse.data);
//             setJobQuestions(questionResponse.data || []);
//         } catch (error) {
//             console.error("Error fetching answers:", error);
//         } finally {
//             setIsLoadingAnswers(false);
//         }
//     };
    
//     const createDonutChart = (data, chartId) => {
//         const ctx = document.getElementById(chartId)?.getContext("2d");
//         if (!ctx || !data || typeof data.correct_count !== 'number' || typeof data.wrong_count !== 'number') return null;
        
//         return new Chart(ctx, {
//             type: 'doughnut',
//             data: {
//                 labels: [`Correct (${data.correct_count})`, `Wrong (${data.wrong_count})`],
//                 datasets: [{
//                     data: [data.correct_count, data.wrong_count],
//                     backgroundColor: ['rgba(40, 167, 69, 0.7)', 'rgba(220, 53, 69, 0.7)'],
//                 }]
//             },
//             options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { display: false } } }
//         });
//     };

//     return (
//         <Modal show={show} onHide={onHide} dialogClassName="modal-xl" centered scrollable>
//             <Modal.Header closeButton><Modal.Title className="gradient-text">{applicant?.first_name || 'Applicant'} Details</Modal.Title></Modal.Header>
//             <Modal.Body className="p-4">
//                 <Card className="mb-4 shadow-sm"><Card.Header><h5 className="mb-0">Job Applications</h5></Card.Header>
//                     <Card.Body className="p-0">
//                         <Table striped bordered hover responsive size="sm" className="mb-0">
//                             <thead><tr><th>Job Title</th><th>Status</th><th>Applied On</th></tr></thead>
//                             <tbody>
//                                 {jobapplications.length > 0 ? jobapplications.map(jobApp => {
//                                     const statusOption = jobStatusOptions.find(opt => opt.value === jobApp.job_status);
//                                     return (
//                                         <tr key={jobApp.id}>
//                                             <td>{jobApp.job_title || 'N/A'}</td>
//                                             <td>
//                                                 <Form.Select size="sm" className={`${statusOption?.bgColor || ''}`} value={jobApp.job_status} onChange={(e) => handleStatusChange(jobApp.id, e.target.value)} style={{ color: statusOption?.bgColor.includes('text-dark') ? '#000' : '#fff' }}>
//                                                     {jobStatusOptions.map(option => <option key={option.value} value={option.value} style={{ backgroundColor: '#fff', color: '#000' }}>{option.label}</option>)}
//                                                 </Form.Select>
//                                             </td>
//                                             <td>{jobApp.applied_date ? new Date(jobApp.applied_date).toLocaleDateString() : 'N/A'}</td>
//                                         </tr>
//                                     );
//                                 }) : <tr><td colSpan="3" className="text-center p-3">No job applications found.</td></tr>}
//                             </tbody>
//                         </Table>
//                     </Card.Body>
//                 </Card>
//                 <h4 className="gradient-text text-center mt-4 mb-3">Personality Assessments</h4>
//                 <div className="d-flex flex-wrap justify-content-center gap-3">
//                     {personalityData.length > 0 ? personalityData.map((data, index) => (
//                         data && data.job_id ? (
//                             <Card key={`${data.job_id}-${index}`} className="text-center shadow-sm" style={{ minWidth: '260px', flex: '1 1 260px' }}>
//                                 <Card.Header><Card.Title as="h6" className="mb-0 text-truncate">{personalityJobTitles[index] || `Assessment ID: ${data.job_id}`}</Card.Title></Card.Header>
//                                 <Card.Body className="p-2">
//                                     <div style={{ height: '150px', width: '100%' }}><canvas id={`personalityChart-${applicant?.id}-${index}`}></canvas></div>
//                                     <p className="mb-1"><strong>Score:</strong> <b className="gradient-text">{typeof data.status === 'number' ? `${data.status.toFixed(1)}%` : 'N/A'}</b></p>
//                                     <Button variant="outline-info" size="sm" onClick={() => handleShowSubmittedAnswers(applicant.id, data.job_id)} disabled={isLoadingAnswers && currentJobId === data.job_id}>
//                                         {isLoadingAnswers && currentJobId === data.job_id ? <Spinner as="span" size="sm" /> : "View Answers"}
//                                     </Button>
//                                 </Card.Body>
//                             </Card>
//                         ) : null
//                     )) : <p className="text-center text-muted">No assessment data available.</p>}
//                 </div>
//                 {currentJobId && (
//                     <div className="mt-4"><Card className="shadow-sm"><Card.Header>
//                         <h5 className="mb-0">Submitted Answers for Job: {personalityJobTitles[personalityData.findIndex(p => p.job_id === currentJobId)] || `ID ${currentJobId}`}</h5>
//                     </Card.Header><Card.Body>
//                         {isLoadingAnswers ? <div className="text-center p-4"><Spinner /></div> : 
//                         filteredSubmittedAnswers && jobQuestions ? (
//                             <Table striped bordered responsive size="sm">
//                                 <thead><tr><th>#</th><th>Question</th><th>Your Answer</th><th>Result</th></tr></thead>
//                                 <tbody>
//                                     {jobQuestions.map((q, i) => {
//                                         const subAns = filteredSubmittedAnswers.selected_answers?.find(a => a.question_id === q.id);
//                                         const isCorrect = subAns && subAns.selected_answer_index === q.correct_answer_index;
//                                         return (
//                                             <tr key={q.id}>
//                                                 <td>{i + 1}</td><td>{q.question_text}</td>
//                                                 <td>{subAns ? (q.answers[subAns.selected_answer_index] || subAns.selected_answer_text) : <i>Not Answered</i>}</td>
//                                                 <td className="text-center">{subAns ? <span className={`badge ${isCorrect ? 'bg-success' : 'bg-danger'}`}>{isCorrect ? 'Correct' : 'Wrong'}</span> : <span className="badge bg-secondary">N/A</span>}</td>
//                                             </tr>
//                                         );
//                                     })}
//                                 </tbody>
//                             </Table>
//                         ) : <p className="text-center p-3">Could not load answers.</p>}
//                     </Card.Body></Card></div>
//                 )}
//             </Modal.Body>
//             <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
//         </Modal>
//     );
// };


// // --- YOUR MAIN COMPONENT ---
// function InterviewerDashboard() {
//     const [interviewSchedule, setInterviewSchedule] = useState([]);
//     const [appliedJobs, setAppliedJobs] = useState({});
//     const [currentPage, setCurrentPage] = useState(1);
//     const itemsPerPage = 5;
//     const [searchQuery, setSearchQuery] = useState("");
//     const [loadingTable, setLoadingTable] = useState(true);
//     const [showModal, setShowModal] = useState(false);
//     const [selectedApplicant, setSelectedApplicant] = useState(null);
//     const [loadingView, setLoadingView] = useState({});
//     const [loadingResume, setLoadingResume] = useState({});

//     // State for the "Results" modal
//     const [showResultsModal, setShowResultsModal] = useState(false);
//     const [selectedApplicantForResults, setSelectedApplicantForResults] = useState(null);
//     const [jobApplicationsForModal, setJobApplicationsForModal] = useState([]);
//     const [loadingResults, setLoadingResults] = useState({});
//     const [successMessage, setSuccessMessage] = useState("");

//     useEffect(() => { fetchInterviewSchedule(); }, []);

//     const fetchInterviewSchedule = () => {
//         const userEmail = localStorage.getItem("email");
//         if (!userEmail) { setLoadingTable(false); return; }
//         setLoadingTable(true);
//         fetch(`https://raasbackend.vetrinahealthcare.com/interviewers/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: userEmail }) })
//             .then(res => res.json())
//             .then(data => {
//                 const formattedData = data.map(interview => ({ ...interview, date: formatDate(interview.date), time: formatTime(interview.time) }));
//                 setInterviewSchedule(formattedData);
//                 fetchAppliedJobsForSchedule(formattedData);
//             })
//             .catch(error => console.error("Error fetching schedule:", error))
//             .finally(() => setLoadingTable(false));
//     };

//     const fetchAppliedJobsForSchedule = (schedule) => {
//         schedule.forEach(interview => {
//             fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${interview.candidate_email}/`)
//                 .then(res => res.json())
//                 .then(data => setAppliedJobs(prev => ({ ...prev, [interview.candidate_email]: data.job_titles || ["N/A"] })))
//                 .catch(error => console.error("Error fetching applied jobs:", error));
//         });
//     };

//     const handleViewResultsClick = async (interview) => {
//         const email = interview.candidate_email;
//         if (!email) return;
//         setLoadingResults(prev => ({ ...prev, [email]: true }));
//         try {
//             const applicantRes = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
//             if (!applicantRes.data || !applicantRes.data.id) throw new Error("Applicant ID not found.");
//             setSelectedApplicantForResults(applicantRes.data);
//             const jobsRes = await axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantRes.data.id}/`);
//             setJobApplicationsForModal(jobsRes.data || []);
//             setShowResultsModal(true);
//         } catch (error) {
//             console.error("Error fetching data for results modal:", error);
//         } finally {
//             setLoadingResults(prev => ({ ...prev, [email]: false }));
//         }
//     };
    
//     const updateJobApplicationStatusInList = (jobAppId, newStatus) => {
//         setJobApplicationsForModal(prev => prev.map(app => app.id === jobAppId ? { ...app, job_status: newStatus } : app));
//     };

//     const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB');
//     const formatTime = (timeString) => new Date(`01/01/2022 ${timeString}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

//     const handleViewClick = (email) => {
//         setLoadingView({ ...loadingView, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.json()).then(data => { setSelectedApplicant(data); setShowModal(true); })
//             .catch(err => console.error("Error fetching applicant data:", err))
//             .finally(() => setLoadingView({ ...loadingView, [email]: false }));
//     };

//     const handleViewResume = (email) => {
//         setLoadingResume({ ...loadingResume, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.blob()).then(blob => window.open(URL.createObjectURL(blob), "_blank"))
//             .catch(err => console.error("Error fetching resume:", err))
//             .finally(() => setLoadingResume({ ...loadingResume, [email]: false }));
//     };

//     const filteredSchedule = interviewSchedule.filter(i => Object.values(i).some(val => String(val).toLowerCase().includes(searchQuery.toLowerCase())) || appliedJobs[i.candidate_email]?.some(j => j.toLowerCase().includes(searchQuery.toLowerCase())));
//     const currentItems = filteredSchedule.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
//     const totalPages = Math.ceil(filteredSchedule.length / itemsPerPage);

//     return (
//         <>
//             <DashNavbar />
//             <div className="wrapper">
//                 <Sidebar />
//                 <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//                     <div className="container mt-4">
//                         <div className="text-start"><h3>Welcome to your Dashboard</h3><p>Below is your interview schedule:</p></div>
//                         <div className="auth-inner mt-5 w-100" style={{ padding: "10px" }}>
//                             <h2 className="mb-4">Interview Schedule</h2>
//                             <div className="row mb-3"><div className="col-12 col-md-6 mx-auto"><InputGroup><Form.Control type="search" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></InputGroup></div></div>
//                             <div className="table-responsive">
//                                 <Table striped bordered hover size="sm">
//                                     <thead className="thead-dark"><tr><th>Sr.No</th><th>Candidate</th><th>Date</th><th>Time</th><th>Applied Job</th><th>Meeting</th><th>Actions</th></tr></thead>
//                                     <tbody>
//                                         {loadingTable ? (<tr><td colSpan="7" className="text-center p-4"><Spinner /></td></tr>)
//                                         : currentItems.length > 0 ? (currentItems.map((interview, index) => (
//                                             <tr key={interview.id || index}>
//                                                 <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                                                 <td>{interview.candidate}</td>
//                                                 <td>{interview.date}</td>
//                                                 <td>{interview.time}</td>
//                                                 <td>{appliedJobs[interview.candidate_email]?.join(", ")}</td>
//                                                 <td><Link to={interview.meetLink} target="_blank" className="btn btn-primary btn-sm">Join</Link></td>
//                                                 <td>
//                                                     <div className="d-flex flex-sm-row flex-column">
//                                                         <Button variant="secondary" size="sm" className="me-sm-2 mb-2 mb-sm-0" onClick={() => handleViewClick(interview.candidate_email)} disabled={loadingView[interview.candidate_email]}>{loadingView[interview.candidate_email] ? <Spinner size="sm"/> : "View"}</Button>
//                                                         <Button variant="info" size="sm" className="me-sm-2 mb-2 mb-sm-0 text-white" onClick={() => handleViewResultsClick(interview)} disabled={loadingResults[interview.candidate_email]}>{loadingResults[interview.candidate_email] ? <Spinner size="sm"/> : "Results"}</Button>
//                                                         <Button variant="secondary" size="sm" onClick={() => handleViewResume(interview.candidate_email)} disabled={loadingResume[interview.candidate_email]}>{loadingResume[interview.candidate_email] ? <Spinner size="sm"/> : "Resume"}</Button>
//                                                     </div>
//                                                 </td>
//                                             </tr>
//                                         ))) : (<tr><td colSpan="7" className="text-center p-4">No interviews found.</td></tr>)}
//                                     </tbody>
//                                 </Table>
//                             </div>
//                             {totalPages > 1 && (<Pagination className="justify-content-center mt-4">{[...Array(totalPages).keys()].map(n=><Pagination.Item key={n+1} active={n+1===currentPage} onClick={()=>setCurrentPage(n+1)}>{n+1}</Pagination.Item>)}</Pagination>)}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//             <ApplicantDetailsModal applicant={selectedApplicant || {}} show={showModal} onHide={() => setShowModal(false)} />
//             {showResultsModal && <ApplicantModal show={showResultsModal} onHide={() => setShowResultsModal(false)} applicant={selectedApplicantForResults} jobapplications={jobApplicationsForModal} setSuccessMessage={setSuccessMessage} updateJobApplication={updateJobApplicationStatusInList} />}
//         </>
//     );
// }

// export default InterviewerDashboard;

// import React, { useState, useEffect } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination } from "react-bootstrap";
// import { Spinner } from "react-bootstrap"; // <-- Added Spinner from react-bootstrap

// function InterviewerDashboard() {
//   const [interviewSchedule, setInterviewSchedule] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [selectedApplicant, setSelectedApplicant] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5;
//   const [searchQuery, setSearchQuery] = useState("");
//   const [loadingTable, setLoadingTable] = useState(true); // <-- New state for table loader
//   const [loadingView, setLoadingView] = useState(""); // <-- New state for View button loader
//   const [loadingResume, setLoadingResume] = useState({});

//   useEffect(() => {
//     fetchInterviewSchedule();
//   }, []);

//   const fetchInterviewSchedule = () => {
//     const userEmail = localStorage.getItem("email");
//     if (!userEmail) {
//       console.error("User email not found in local storage");
//       return;
//     }
//     setLoadingTable(true); // <-- Enable loader before data fetch

//     fetch(`https://raasbackend.vetrinahealthcare.com/interviewers/`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: userEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         const formattedData = data.map((interview) => ({
//           ...interview,
//           date: formatDate(interview.date),
//           time: formatTime(interview.time),
//         }));
//         setInterviewSchedule(formattedData);

//         // Fetch applied jobs for the interview schedule
//         fetchAppliedJobsForSchedule(formattedData);
//       })
//       .catch((error) => {
//         console.error("Error fetching interview schedule:", error);
//       })
//       .finally(() => setLoadingTable(false)); // <-- Disable loader after data fetch
//   };

//   const fetchAppliedJobsForSchedule = (schedule) => {
//     const candidateEmails = schedule.map(
//       (interview) => interview.candidate_email
//     );
//     candidateEmails.forEach((email) => {
//       fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${email}/`)
//         .then((response) => {
//           if (!response.ok) {
//             throw new Error("Network response was not ok");
//           }
//           return response.json();
//         })
//         .then((appliedJobsResponse) => {
//           setAppliedJobs((prevJobs) => ({
//             ...prevJobs,
//             [email]: appliedJobsResponse.job_titles || ["Not Applied"],
//           }));
//         })
//         .catch((error) => {
//           console.error("Error fetching applied jobs:", error);
//         });
//     });
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const formattedDate = `${date.getDate()}-${date.getMonth() + 1
//       }-${date.getFullYear()}`;
//     return formattedDate;
//   };

//   const formatTime = (timeString) => {
//     const time = new Date(`01/01/2022 ${timeString}`);
//     const formattedTime = time.toLocaleTimeString("en-US", {
//       hour: "numeric",
//       minute: "2-digit",
//     });
//     return formattedTime;
//   };

//   const handleViewClick = (candidateEmail) => {
//     setLoadingView((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         setSelectedApplicant(data);
//         setShowModal(true);
//       })
//       .catch((error) => {
//         console.error("Error fetching applicant data:", error);
//       })
//       .finally(() => {
//         setLoadingView((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });

//   };

//   const handleViewResume = (candidateEmail) => {
//     setLoadingResume((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.blob();
//       })
//       .then((blob) => {
//         const resumeBlobUrl = URL.createObjectURL(blob);
//         window.open(resumeBlobUrl, "_blank");
//       })
//       .catch((error) => {
//         console.error("Error fetching resume:", error);
//       })
//       .finally(() => {
//         setLoadingResume((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });
//   };

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = interviewSchedule.slice(
//     indexOfFirstItem,
//     indexOfLastItem
//   );

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const totalPages = Math.ceil(interviewSchedule.length / itemsPerPage);
//   const handleSearch = (e) => {
//     setSearchQuery(e.target.value);
//   };

//   const filteredSchedule = interviewSchedule.filter((interview) => {
//     const candidateMatch = interview.candidate
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const dateMatch = interview.date
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const appliedJobMatch = appliedJobs[interview.candidate_email]?.some(
//       (job) => job.toLowerCase().includes(searchQuery.toLowerCase())
//     );
//     return candidateMatch || dateMatch || appliedJobMatch;
//   });

//   return (
//     <>
//       {/* <div
//         style={{ display: "flex", flexDirection: "column", height: "100vh" }}
//       > */}
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//           <div className="container mt-4">
//             <div className="text-start">
//               <h3>Welcome to your Dashboard</h3>
//               <p>Below is your interview schedule:</p>
//             </div>
//             <div className="auth-inner mt-5 w-100">
//               <h2 className="mb-4">Interview Schedule</h2>
//               <div className="input-group justify-content-center align-items-center">
//                 <div className="form-outline w-50">
//                   <input
//                     type="search"
//                     className="form-control"
//                     placeholder="Search by Name or Email"
//                     onChange={handleSearch}
//                   />
//                   <button className="btn btn-primary searchicon text-xs">
//                     <i className="fas fa-search"></i>
//                   </button>
//                 </div>
//               </div>
//               <div className="table-responsive">
//                 <table className="table">
//                   <thead className="thead-dark">
//                     <tr>
//                       <th scope="col">Sr.No</th>
//                       <th scope="col">Candidate</th>
//                       <th scope="col">Date</th>
//                       <th scope="col">Time</th>
//                       <th scope="col">Applied Job</th>
//                       <th scope="col">Meeting Link</th>
//                       <th scope="col">Actions</th>
//                     </tr>
//                   </thead>
//                   {loadingTable ? ( // <-- Conditional rendering for loader
//                     <tbody>
//                       <tr>
//                         <td colSpan="7" className="text-center">
//                           <Spinner animation="border" /> {/* <-- Spinner */}
//                         </td>
//                       </tr>
//                     </tbody>
//                   ) : (
//                     <tbody>
//                       {filteredSchedule
//                         .slice(indexOfFirstItem, indexOfLastItem)
//                         .map((interview, index) => (
//                           <tr key={index}>
//                             <td>{indexOfFirstItem + index + 1}</td>
//                             <td>{interview.candidate}</td>
//                             <td>{interview.date}</td>
//                             <td>{interview.time}</td>
//                             <td className="w-25">
//                               {appliedJobs[interview.candidate_email]?.join(", ")}
//                             </td>
//                             <td>
//                               <Link
//                                 to={interview.meetLink}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="btn btn-primary"
//                               >
//                                 Join Meeting
//                               </Link>
//                             </td>
//                             <td>
//                               <button
//                                 className="btn btn-secondary me-2 mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewClick(interview.candidate_email)
//                                 }

//                                 disabled={loadingView[interview.candidate_email]} // Disable button based on specific email

//                               >

//                                 {loadingView[interview.candidate_email] ? ( // Show spinner only for the loading button
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "View"
//                                 )}

//                               </button>
//                               <button
//                                 className="btn btn-secondary mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewResume(interview.candidate_email)
//                                 }
//                                 disabled={loadingResume[interview.candidate_email]} // Disable button based on specific email
//                               >
//                                 {loadingResume[interview.candidate_email] ? ( // Show spinner only for the loading button
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "Resume"
//                                 )}

//                               </button>
//                             </td>
//                           </tr>
//                         ))}
//                     </tbody>
//                   )}
//                 </table>
//               </div>
//               <Pagination className="mt-4">
//                 {/* <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} /> */}
//                 <Pagination.Prev
//                   onClick={() => paginate(currentPage - 1)}
//                   disabled={currentPage === 1}
//                 />
//                 {Array.from({ length: totalPages }, (_, i) => (
//                   <Pagination.Item
//                     key={i + 1}
//                     active={i + 1 === currentPage}
//                     onClick={() => paginate(i + 1)}
//                   >
//                     {i + 1}
//                   </Pagination.Item>
//                 ))}
//                 <Pagination.Next
//                   onClick={() => paginate(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                 />
//                 {/* <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} /> */}
//               </Pagination>
//             </div>
//           </div>
//         </div>
//       </div>
//       {/* </div> */}

//       <ApplicantDetailsModal
//         applicant={selectedApplicant || {}}
//         show={showModal}
//         onHide={() => setShowModal(false)}
//       />
//     </>
//   );
// }

// export default InterviewerDashboard;

//old code

// import React, { useState, useEffect } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination } from "react-bootstrap";
// import { Spinner } from "react-bootstrap"; // <-- Added Spinner from react-bootstrap

// function InterviewerDashboard() {
//   const [interviewSchedule, setInterviewSchedule] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [selectedApplicant, setSelectedApplicant] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5;
//   const [searchQuery, setSearchQuery] = useState("");
//   const [loadingTable, setLoadingTable] = useState(true); // <-- New state for table loader
//   const [loadingView, setLoadingView] = useState(""); // <-- New state for View button loader
//   const [loadingResume, setLoadingResume] = useState({});

//   useEffect(() => {
//     fetchInterviewSchedule();
//   }, []);

//   const fetchInterviewSchedule = () => {
//     const userEmail = localStorage.getItem("email");
//     if (!userEmail) {
//       console.error("User email not found in local storage");
//       return;
//     }
//     setLoadingTable(true); // <-- Enable loader before data fetch

//     fetch(`https://raasbackend.vetrinahealthcare.com/interviewers/`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: userEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         const formattedData = data.map((interview) => ({
//           ...interview,
//           date: formatDate(interview.date),
//           time: formatTime(interview.time),
//         }));
//         setInterviewSchedule(formattedData);

//         // Check if no interviews are scheduled and set a flag
//         if (formattedData.length === 0) {
//           setInterviewSchedule(null); // <-- Set interview schedule as null if empty
//         } else {
//           // Fetch applied jobs for the interview schedule
//           fetchAppliedJobsForSchedule(formattedData);
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching interview schedule:", error);
//       })
//       .finally(() => setLoadingTable(false)); // <-- Disable loader after data fetch
//   };

//   const fetchAppliedJobsForSchedule = (schedule) => {
//     const candidateEmails = schedule.map(
//       (interview) => interview.candidate_email
//     );
//     candidateEmails.forEach((email) => {
//       fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${email}/`)
//         .then((response) => {
//           if (!response.ok) {
//             throw new Error("Network response was not ok");
//           }
//           return response.json();
//         })
//         .then((appliedJobsResponse) => {
//           setAppliedJobs((prevJobs) => ({
//             ...prevJobs,
//             [email]: appliedJobsResponse.job_titles || ["Not Applied"],
//           }));
//         })
//         .catch((error) => {
//           console.error("Error fetching applied jobs:", error);
//         });
//     });
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const formattedDate = `${date.getDate()}-${date.getMonth() + 1
//       }-${date.getFullYear()}`;
//     return formattedDate;
//   };

//   const formatTime = (timeString) => {
//     const time = new Date(`01/01/2022 ${timeString}`);
//     const formattedTime = time.toLocaleTimeString("en-US", {
//       hour: "numeric",
//       minute: "2-digit",
//     });
//     return formattedTime;
//   };

//   const handleViewClick = (candidateEmail) => {
//     setLoadingView((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         setSelectedApplicant(data);
//         setShowModal(true);
//       })
//       .catch((error) => {
//         console.error("Error fetching applicant data:", error);
//       })
//       .finally(() => {
//         setLoadingView((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });

//   };

//   const handleViewResume = (candidateEmail) => {
//     setLoadingResume((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.blob();
//       })
//       .then((blob) => {
//         const resumeBlobUrl = URL.createObjectURL(blob);
//         window.open(resumeBlobUrl, "_blank");
//       })
//       .catch((error) => {
//         console.error("Error fetching resume:", error);
//       })
//       .finally(() => {
//         setLoadingResume((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });
//   };

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = interviewSchedule ? interviewSchedule.slice(
//     indexOfFirstItem,
//     indexOfLastItem
//   ) : [];

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const totalPages = interviewSchedule ? Math.ceil(interviewSchedule.length / itemsPerPage) : 1;

//   const handleSearch = (e) => {
//     setSearchQuery(e.target.value);
//   };

//   const filteredSchedule = interviewSchedule ? interviewSchedule.filter((interview) => {
//     const candidateMatch = interview.candidate
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const dateMatch = interview.date
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const appliedJobMatch = appliedJobs[interview.candidate_email]?.some(
//       (job) => job.toLowerCase().includes(searchQuery.toLowerCase())
//     );
//     return candidateMatch || dateMatch || appliedJobMatch;
//   }) : [];

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//           <div className="container mt-4">
//             <div className="text-start">
//               <h3>Welcome to your Dashboard</h3>
//               <p>Below is your interview schedule:</p>
//             </div>
//             <div className="auth-inner mt-5 w-100">
//               <h2 className="mb-4">Interview Schedule</h2>
//               <div className="input-group justify-content-center align-items-center">
//                 <div className="form-outline w-50">
//                   <input
//                     type="search"
//                     className="form-control"
//                     placeholder="Search by Name or Email"
//                     onChange={handleSearch}
//                   />
//                   <button className="btn btn-primary searchicon text-xs">
//                     <i className="fas fa-search"></i>
//                   </button>
//                 </div>
//               </div>
//               <div className="table-responsive">
//                 <table className="table">
//                   <thead className="thead-dark">
//                     <tr>
//                       <th scope="col">Sr.No</th>
//                       <th scope="col">Candidate</th>
//                       <th scope="col">Date</th>
//                       <th scope="col">Time</th>
//                       <th scope="col">Applied Job</th>
//                       <th scope="col">Meeting Link</th>
//                       <th scope="col">Actions</th>
//                     </tr>
//                   </thead>
//                   {loadingTable ? (
//                     <tbody>
//                       <tr>
//                         <td colSpan="7" className="text-center">
//                           <Spinner animation="border" />
//                         </td>
//                       </tr>
//                     </tbody>
//                   ) : interviewSchedule === null ? (
//                     <tbody>
//                       <tr>
//                         <td colSpan="7" className="text-center">
//                           Your interview has not been scheduled with any candidate.
//                         </td>
//                       </tr>
//                     </tbody>
//                   ) : (
//                     <tbody>
//                       {filteredSchedule
//                         .slice(indexOfFirstItem, indexOfLastItem)
//                         .map((interview, index) => (
//                           <tr key={index}>
//                             <td>{indexOfFirstItem + index + 1}</td>
//                             <td>{interview.candidate}</td>
//                             <td>{interview.date}</td>
//                             <td>{interview.time}</td>
//                             <td className="w-25">
//                               {appliedJobs[interview.candidate_email]?.join(", ")}
//                             </td>
//                             <td>
//                               <Link
//                                 to={interview.meetLink}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="btn btn-primary"
//                               >
//                                 Join Meeting
//                               </Link>
//                             </td>
//                             <td>
//                               <button
//                                 className="btn btn-secondary me-2 mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewClick(interview.candidate_email)
//                                 }
//                                 disabled={loadingView[interview.candidate_email]}
//                               >
//                                 {loadingView[interview.candidate_email] ? (
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "View"
//                                 )}
//                               </button>
//                               <button
//                                 className="btn btn-secondary mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewResume(interview.candidate_email)
//                                 }
//                                 disabled={loadingResume[interview.candidate_email]}
//                               >
//                                 {loadingResume[interview.candidate_email] ? (
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "Resume"
//                                 )}
//                               </button>
//                             </td>
//                           </tr>
//                         ))}
//                     </tbody>
//                   )}
//                 </table>
//               </div>
//               <Pagination className="mt-4">
//                 <Pagination.Prev
//                   onClick={() => paginate(currentPage - 1)}
//                   disabled={currentPage === 1}
//                 />
//                 {Array.from({ length: totalPages }, (_, i) => (
//                   <Pagination.Item
//                     key={i + 1}
//                     active={i + 1 === currentPage}
//                     onClick={() => paginate(i + 1)}
//                   >
//                     {i + 1}
//                   </Pagination.Item>
//                 ))}
//                 <Pagination.Next
//                   onClick={() => paginate(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                 />
//               </Pagination>
//             </div>
//           </div>
//         </div>
//       </div>

//       <ApplicantDetailsModal
//         applicant={selectedApplicant || {}}
//         show={showModal}
//         onHide={() => setShowModal(false)}
//       />
//     </>
//   );
// }

// export default InterviewerDashboard;
